import axios from 'axios';
import './Dashboard.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary, getPaymentSummary } from '../../utils/helpers';
import { getAvailableUrns, getAvailableKeepsakes, calculateBaseServicePrice, getProductPrice, getCustomProductIcon, getProductIcon, formatKeepsakesForDisplay, parseQuantityFromString, convertToCartFormat, calculateTotalCost, initializePricing } from '../../utils/pricingHelpers';
import { SearchableMultiSelect, SearchableSelect, VetClinicCombobox } from '../forms/FormUtils';
import MultiItemSelector from '../forms/MultiItemSelector';
import QRScannerModal from '../qr/QRScannerModal';
import QuickScanViewModal from '../qr/QuickScanViewModal';
import KeepsakesCollectionModal from '../keepsakes/KeepsakesCollectionModal';
import SMSPreviewModal from '../communications/SMSPreviewModal';
import EmailPreviewModal from '../communications/EmailPreviewModal';
import { getSignatureBlock, updateAfterpayDraft } from '../communications/emailSignature';
import CalendarView from '../calendar/CalendarView';
import AddPetModal from '../pets/AddPetModal';
import QuickLocationModal from '../common/QuickLocationModal';
import WorkflowModal from '../workflow/WorkflowModal';
import { WorkflowQuickUpdate } from '../workflow/WorkflowQuickUpdate';
import { QuickLocationUpdateButton } from '../workflow/QuickLocationUpdateButton';
import OnlineSalesView from '../online-sales/OnlineSalesView';
import ClinicRewardsView from '../clinic-rewards/ClinicRewardsView';
import SimpleAnalyticsView from '../analytics/SimpleAnalyticsView';
import WalkInFormModal from '../pets/WalkInFormModal';
import { useAuth } from '../../context/AuthContext';
import { useRouter } from '../common/Router';
import LogoutButton from '../auth/LogoutButton';
import OnlineSalesModal from '../online-sales/OnlineSalesModal';
import OrderHistoryModal from '../online-sales/OrderHistoryModal';
import ThankYouModal from '../common/ThankYouModal';
import AIChatPanel from '../ai/AIChatPanel';
import Pet400View from '../pet400/Pet400View';
import InventoryModal from '../inventory/InventoryModal';
import PricingAdminModal from '../pricing/PricingAdminModal';
import NotificationSettingsModal from '../notifications/NotificationSettingsModal';
import ProspectsView from '../prospects/ProspectsView';


const Dashboard = () => {
  const { user } = useAuth();
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [stats, setStats] = useState(null);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPets, setSelectedPets] = useState(new Set()); // For bulk operations
  const [showFilters, setShowFilters] = useState(false); // For mobile filter toggle
  
  const [filters, setFilters] = useState(() => {
    // Load filters from session storage
    const saved = sessionStorage.getItem('petFilters');
    return saved ? JSON.parse(saved) : { status: '', location: '', search: '', paid: '', prepayment: '' };
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showClinicRewards, setShowClinicRewards] = useState(false);
  const [showPet400, setShowPet400] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showOnlineSales, setShowOnlineSales] = useState(false);
  const [onlineSalesTab, setOnlineSalesTab] = useState('orders');
  const [showWalkInForm, setShowWalkInForm] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarInitialView, setCalendarInitialView] = useState('grid'); // 'grid' or 'timeslot'
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scannedPet, setScannedPet] = useState(null);
  const [showKeepsakesModal, setShowKeepsakesModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [showTransportationModal, setShowTransportationModal] = useState(null);
  const [showQuickNotesModal, setShowQuickNotesModal] = useState(null);
  const [workflowPet, setWorkflowPet] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set()); // Track which cards are expanded
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');
  const [customerLookup, setCustomerLookup] = useState({ found: false, data: null, loading: false });
  
  // Real-time updates state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const wsRef = useRef(null);
  
  // SMS Workflow States
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsModalData, setSMSModalData] = useState({
    petId: null,
    stepKey: null,
    templateKey: null,
    primaryTemplate: null,
    alternativeTemplates: [],
    renderedBody: '',
    clientName: '',
    petName: '',
    toPhone: '',
    loading: false,
    sending: false,
    error: null,
    success: false
  });
  
  // Email Workflow States
  const [showEmailModal, setShowEmailModal] = useState(false);

  const [communications, setCommunications] = useState([]);

  // Helper functions for collapsible cards
  const toggleCardExpansion = (petId) => {
    const newExpandedCards = new Set(expandedCards);
    if (newExpandedCards.has(petId)) {
      newExpandedCards.delete(petId);
    } else {
      newExpandedCards.add(petId);
    }
    setExpandedCards(newExpandedCards);
  };

  // SMS Workflow Functions
  const openSMSModal = async (petId, stepKey) => {
    try {
      setSMSModalData(prev => ({ ...prev, petId, stepKey, loading: true, error: null, success: false, renderedBody: '' }));
      setShowSMSModal(true);
      
      // Get templates for this step
      const templatesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/templates/${stepKey}`);
      if (!templatesResponse.ok) {
        throw new Error('Failed to load SMS templates');
      }
      const templatesData = await templatesResponse.json();
      
      // Preview SMS with primary template
      const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: petId,
          template_key: templatesData.primary_template.key
        })
      });
      
      if (!previewResponse.ok) {
        throw new Error('Failed to preview SMS');
      }
      const previewData = await previewResponse.json();
      
      setSMSModalData({
        petId,
        stepKey,
        templateKey: templatesData.primary_template.key,
        primaryTemplate: templatesData.primary_template,
        alternativeTemplates: templatesData.alternative_templates || [],
        renderedBody: previewData.body_rendered,
        clientName: previewData.client_name,
        petName: previewData.pet_name,
        toPhone: previewData.to_phone,
        loading: false,
        sending: false,
        error: null,
        success: false
      });
      
    } catch (error) {
      console.error('Error opening SMS modal:', error);
      setSMSModalData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };
  
  const changeTemplate = async (newTemplateKey) => {
    try {
      setSMSModalData(prev => ({ ...prev, loading: true, error: null }));
      
      const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: smsModalData.petId,
          template_key: newTemplateKey
        })
      });
      
      if (!previewResponse.ok) {
        throw new Error('Failed to preview SMS with new template');
      }
      const previewData = await previewResponse.json();
      
      setSMSModalData(prev => ({
        ...prev,
        templateKey: newTemplateKey,
        renderedBody: previewData.body_rendered,
        loading: false
      }));
      
    } catch (error) {
      console.error('Error changing template:', error);
      setSMSModalData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };
  
  const sendSMS = async (scheduledTime = null) => {
    try {
      setSMSModalData(prev => ({ ...prev, sending: true, error: null }));
      
      // Prepare scheduled time for backend
      // datetime-local returns: "2025-11-17T15:21" (no timezone)
      // Backend will interpret this as AEST and convert to UTC
      const payload = {
        record_id: smsModalData.petId,
        template_key: smsModalData.templateKey,
        step_key: smsModalData.stepKey,
        body_rendered: smsModalData.renderedBody,
        sent_by_user_id: user?.id
      };
      
      if (scheduledTime) {
        // Send the datetime string as-is - backend will handle AEST→UTC conversion
        payload.scheduled_send_time = scheduledTime;
        console.log('🕐 Scheduling SMS for (AEST):', scheduledTime);
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send SMS');
      }
      
      const result = await response.json();
      
      // Show appropriate success message
      if (result.scheduled) {
        showToast(`📅 SMS scheduled for ${new Date(scheduledTime).toLocaleString('en-AU', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })}`, 'success');
      } else {
        showToast('✅ SMS sent successfully!', 'success');
      }
      
      setSMSModalData(prev => ({
        ...prev,
        sending: false,
        success: true,
        scheduled: !!result.scheduled
      }));
      
      // Refresh pets to show updated workflow status (only if sent immediately)
      if (!result.scheduled) {
        await fetchPets();
        
        // If workflow modal is open for this pet, refresh its data
        if (workflowPet && workflowPet.id === smsModalData.petId) {
          const response = await apiClient.get(`/pets/${smsModalData.petId}`);
          setWorkflowPet(response.data);
        }
      }
      
      // Auto-close modal after 2 seconds
      setTimeout(() => {
        setShowSMSModal(false);
        setSMSModalData({
          petId: null,
          stepKey: null,
          templateKey: null,
          primaryTemplate: null,
          alternativeTemplates: [],
          renderedBody: '',
          clientName: '',
          petName: '',
          toPhone: '',
          loading: false,
          sending: false,
          error: null,
          success: false
        });
      }, 2000);
      
    } catch (error) {
      console.error('Error sending SMS:', error);
      setSMSModalData(prev => ({
        ...prev,
        sending: false,
        error: error.message
      }));
    }
  };
  
  const fetchCommunications = async (petId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/communications/${petId}`);
      if (response.ok) {
        const data = await response.json();
        setCommunications(data.communications || []);
      }
    } catch (error) {
      console.error('Error fetching communications:', error);
    }
  };


  // Email Modal State
  const [emailModalData, setEmailModalData] = useState({
    petId: null,
    stepKey: null,
    templateKey: null,
    subject: '',
    renderedBody: '',
    clientName: '',
    petName: '',
    toEmail: '',
    loading: false,
    sending: false,
    error: null,
    success: false,
    availableTemplates: [],
    previousPetInfo: null
  });

  // Email Workflow Functions
  const openEmailModal = async (petId, stepKey) => {
    try {
      setEmailModalData(prev => ({
        ...prev,
        loading: true,
        error: null,
        availableTemplates: [],
        previousPetInfo: null,
        templateKey: null,
        subject: '',
        renderedBody: '',
        category: null,
      }));
      setShowEmailModal(true);
      
      const categoryMap = {
        'tree_planting_email': 'tree_planting',
        'tree_planting_reply_email': 'tree_planting_reply',
        'other_emails': 'other_emails',
        'review_request': 'review_request'
      };
      const category = categoryMap[stepKey];
      
      if (category) {
        const templatesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/templates/${category}?record_id=${petId}`);
        if (!templatesResponse.ok) throw new Error('Failed to fetch email templates');
        const templatesData = await templatesResponse.json();
        
        const defaultTemplate = templatesData.templates[0];
        const templateKey = defaultTemplate.key;

        // Pre-load afterpay_link from pet record (for AfterPay template resend)
        let petAfterpayLink = null;
        try {
          const petRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pets/${petId}`);
          if (petRes.ok) {
            const petData = await petRes.json();
            petAfterpayLink = petData.afterpay_link || null;
          }
        } catch {}

        const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ record_id: petId, template_key: templateKey, afterpay_link: petAfterpayLink })
        });
        if (!previewResponse.ok) throw new Error('Failed to preview email');
        const previewData = await previewResponse.json();
        
        setEmailModalData({
          petId,
          stepKey,
          templateKey,
          subject: previewData.subject,
          renderedBody: previewData.body_rendered,
          clientName: previewData.client_name,
          petName: previewData.pet_name,
          toEmail: previewData.to_email,
          signature: previewData.signature,
          signatureHtml: getSignatureBlock(previewData.body_rendered),
          loading: false,
          sending: false,
          error: null,
          success: false,
          availableTemplates: templatesData.templates,
          previousPetInfo: templatesData.previous_pet_info,
          afterpayLink: petAfterpayLink || '',
          category: category
        });
      } else {
        const templateKey = 'email.tree_planting';
        const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ record_id: petId, template_key: templateKey })
        });
        if (!previewResponse.ok) throw new Error('Failed to preview email');
        const previewData = await previewResponse.json();
        
        setEmailModalData({
          petId,
          stepKey,
          templateKey,
          subject: previewData.subject,
          renderedBody: previewData.body_rendered,
          clientName: previewData.client_name,
          petName: previewData.pet_name,
          toEmail: previewData.to_email,
          signature: previewData.signature,
          signatureHtml: getSignatureBlock(previewData.body_rendered),
          loading: false,
          sending: false,
          error: null,
          success: false,
          availableTemplates: [],
          previousPetInfo: null
        });
      }
    } catch (error) {
      console.error('Error opening email modal:', error);
      setEmailModalData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  const changeEmailTemplate = async (templateKey, afterpayLinkOverride) => {
    try {
      setEmailModalData(prev => ({ ...prev, loading: true }));
      const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: emailModalData.petId,
          template_key: templateKey,
          afterpay_link: (afterpayLinkOverride !== undefined ? afterpayLinkOverride : emailModalData.afterpayLink) || null,
          signature: emailModalData.signature
        })
      });
      if (!previewResponse.ok) throw new Error('Failed to preview email');
      const previewData = await previewResponse.json();
      
      setEmailModalData(prev => ({
        ...prev,
        templateKey,
        subject: previewData.subject,
        renderedBody: previewData.body_rendered,
        signature: previewData.signature,
        signatureHtml: getSignatureBlock(previewData.body_rendered),
        loading: false
      }));
    } catch (error) {
      console.error('Error changing email template:', error);
      setEmailModalData(prev => ({ ...prev, loading: false, error: error.message }));
    }
  };

  const sendEmail = async () => {
    try {
      setEmailModalData(prev => ({ ...prev, sending: true, error: null }));
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: emailModalData.petId,
          template_key: emailModalData.templateKey,
          step_key: emailModalData.stepKey,
          subject: emailModalData.subject,
          body_rendered: emailModalData.renderedBody,
          signature: emailModalData.signature,
          sent_by_user_id: user?.id || 'unknown',
          afterpay_link: emailModalData.afterpayLink || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send email');
      }
      
      const data = await response.json();
      
      // Update modal to show success
      setEmailModalData(prev => ({
        ...prev,
        sending: false,
        success: true
      }));
      
      // Fetch updated pet data to refresh UI
      await fetchPets();
      await fetchStats();
      
      // If workflow modal is open for this pet, refresh its data
      if (workflowPet && workflowPet.id === emailModalData.petId) {
        const response = await apiClient.get(`/pets/${emailModalData.petId}`);
        setWorkflowPet(response.data);
      }
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailModalData({
          petId: null,
          stepKey: null,
          templateKey: null,
          subject: '',
          renderedBody: '',
          clientName: '',
          petName: '',
          toEmail: '',
          loading: false,
          sending: false,
          error: null,
          success: false
        });
      }, 2000);
      
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailModalData(prev => ({
        ...prev,
        sending: false,
        error: error.message
      }));
    }
  };

  // Certificate Modal Functions
  const openCertificateModal = async (petId) => {
    try {
      setEmailModalData(prev => ({ ...prev, loading: true, error: null }));
      setShowEmailModal(true);
      
      // Preview certificate email
      const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificates/preview/${petId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!previewResponse.ok) {
        throw new Error('Failed to preview certificate email');
      }
      const previewData = await previewResponse.json();
      
      setEmailModalData({
        petId,
        stepKey: 'certificate_email',
        templateKey: 'certificate_email',
        subject: previewData.subject,
        renderedBody: previewData.body_html,
        signature: previewData.signature,
        signatureHtml: getSignatureBlock(previewData.body_html),
        clientName: previewData.client_name,
        petName: previewData.pet_name,
        toEmail: previewData.to_email,
        loading: false,
        sending: false,
        error: null,
        success: false,
        isCertificateEmail: true,  // Flag to handle different send logic
        envCertType: previewData.environmental_certificate_type
      });
      
    } catch (error) {
      console.error('Error opening certificate modal:', error);
      setEmailModalData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  const sendCertificates = async () => {
    try {
      setEmailModalData(prev => ({ ...prev, sending: true, error: null }));
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificates/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pet_id: emailModalData.petId,
          custom_subject: emailModalData.subject,
          custom_body_html: emailModalData.renderedBody,
          signature: emailModalData.signature
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send certificates');
      }
      
      const data = await response.json();
      
      // Update modal to show success
      setEmailModalData(prev => ({
        ...prev,
        sending: false,
        success: true
      }));
      
      // Show toast with certificate reference
      showToast(`✅ Certificates sent successfully! Reference: ${data.certificate_reference}`, 'success');
      
      // Fetch updated pet data to refresh UI
      await fetchPets();
      await fetchStats();
      
      // If workflow modal is open for this pet, refresh its data
      if (workflowPet && workflowPet.id === emailModalData.petId) {
        const response = await apiClient.get(`/pets/${emailModalData.petId}`);
        setWorkflowPet(response.data);
      }
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailModalData({
          petId: null,
          stepKey: null,
          templateKey: null,
          subject: '',
          renderedBody: '',
          clientName: '',
          petName: '',
          toEmail: '',
          loading: false,
          sending: false,
          error: null,
          success: false,
          isCertificateEmail: false
        });
      }, 2000);
      
    } catch (error) {
      console.error('Error sending certificates:', error);
      setEmailModalData(prev => ({
        ...prev,
        sending: false,
        error: error.message
      }));
    }
  };


  const getWeekOfMonth = (dateStr) => {
    const date = new Date(dateStr);
    
    // Use Monday-Sunday calendar weeks (standard calendar week)
    // Get the first Monday of the month
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstMonday = new Date(firstDayOfMonth);
    
    // Find the first Monday of the month
    while (firstMonday.getDay() !== 1) { // 1 = Monday
      firstMonday.setDate(firstMonday.getDate() + 1);
    }
    
    // Calculate which calendar week this date falls into
    const weekStart = new Date(firstMonday);
    let weekNumber = 1;
    
    while (weekStart <= date) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6); // Sunday
      
      if (date >= weekStart && date <= weekEnd) {
        return weekNumber;
      }
      
      weekStart.setDate(weekStart.getDate() + 7); // Next Monday
      weekNumber++;
    }
    
    // Fallback for dates before first Monday
    return 1;
  };

  const getWeekColor = (weekNumber, arrivalDate, petName = null) => {
    // CALENDAR WEEK-BASED COLOR SYSTEM: All pets in same Monday-Sunday week get same color
    
    // Convert arrival date to proper Monday-Sunday week key (matches backend logic)
    const date = new Date(arrivalDate);
    
    // Calculate Monday-Sunday week boundaries (same logic as backend)
    const days_since_monday = date.getDay() === 0 ? 6 : date.getDay() - 1; // Convert Sunday=0 to Sunday=6, Monday=1 to Monday=0
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - days_since_monday); // Get Monday of this week
    
    // Calculate week number of the year (Monday-based)
    const yearStart = new Date(weekStart.getFullYear(), 0, 1);
    const days = Math.floor((weekStart - yearStart) / (24 * 60 * 60 * 1000));
    const weekOfYear = Math.floor(days / 7) + 1;
    
    console.log(`🎨 Pet ${petName}: Arrival ${arrivalDate}, Week ${weekOfYear} of ${weekStart.getFullYear()}`);
    
    // Week-based color assignment (cycle through 4 colors)
    const weekColors = {
      1: { bg: 'bg-blue-100', border: 'border-blue-500', text: 'text-blue-900', name: 'Blue' },
      2: { bg: 'bg-purple-100', border: 'border-purple-500', text: 'text-purple-900', name: 'Purple' },
      3: { bg: 'bg-teal-100', border: 'border-teal-500', text: 'text-teal-900', name: 'Teal' },
      4: { bg: 'bg-yellow-100', border: 'border-yellow-500', text: 'text-yellow-900', name: 'Yellow' },
    };
    
    // Use week of year to determine color assignment (cycling through 4 colors)
    const colorIndex = ((weekOfYear - 1) % 4) + 1;
    const selectedColor = weekColors[colorIndex];
    
    console.log(`🎨 Pet ${petName} assigned ${selectedColor.name} color (Week ${weekOfYear} → Color ${colorIndex})`);
    
    return `${selectedColor.bg} ${selectedColor.border} ${selectedColor.text}`;
  };

  const { navigate } = useRouter();

  useEffect(() => {
    fetchStats();
    fetchPets();
    initializePricing(API.replace('/api', ''));
  }, [filters]);

  // Save filters to session storage
  useEffect(() => {
    sessionStorage.setItem('petFilters', JSON.stringify(filters));
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // ============================================================
  // Ready for Cremation — operational fast-track signal
  // ============================================================
  // Badge auto-hides once pet enters the chamber or any post-chamber stage.
  const POST_READY_LOCATIONS = new Set([
    'Cremation Chambers', 'Cooling / Ashes Processing', 'Urn Assembly',
    'Ready for Collection', 'Completed',
  ]);

  const isReadyBadgeVisible = (pet) =>
    !!pet?.ready_for_aquacremation &&
    !POST_READY_LOCATIONS.has(pet?.location_area) &&
    pet?.status !== 'Completed';

  const toggleReadyForCremation = async (pet, e) => {
    if (e) e.stopPropagation();
    const next = !pet.ready_for_aquacremation;
    // Optimistic update
    setPets(prev => prev.map(p => p.id === pet.id ? { ...p, ready_for_aquacremation: next } : p));
    try {
      await apiClient.put(`/pets/${pet.id}`, { ready_for_aquacremation: next });
      showToast(next ? `🔥 ${pet.pet_name} marked ready for cremation` : `${pet.pet_name} unmarked`, 'success');
    } catch (err) {
      // Revert on failure
      setPets(prev => prev.map(p => p.id === pet.id ? { ...p, ready_for_aquacremation: !next } : p));
      showToast('Failed to update — please try again', 'error');
    }
  };

  const fetchPets = async () => {
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.location) params.location = filters.location;
      if (filters.search) params.search = filters.search;
      if (filters.paid !== '') params.paid = filters.paid;
      
      // Add cache-busting parameter to ensure fresh data
      params._t = Date.now();
      
      console.log('🔍 API CALL: Making request to /pets with params:', params);
      
      const response = await apiClient.get('/pets', { 
        params,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      console.log('📥 API RESPONSE: Received', response.data.length, 'pets');
      console.log('📋 PETS RECEIVED:', response.data.map(p => `${p.pet_name}(${p.status})`));
      
      // OPTION 1: SIMPLE VISUAL SOLUTION - No sorting, just obvious visual indicators
      const allPets = response.data;
      
      // Basic sort: Just by date (newest first) - keep it simple
      const sortedPets = allPets.sort((a, b) => {
        const dateA = new Date(a.arrival_date);
        const dateB = new Date(b.arrival_date);
        return dateB.getTime() - dateA.getTime(); // Newest first
      });
      
      console.log('🎯 AFTER SORTING:', sortedPets.map(p => `${p.pet_name}(${p.status})`));
      
      // Add queue position to each pet
      const petsWithQueuePosition = sortedPets.map((pet, index) => ({
        ...pet,
        queuePosition: index + 1
      }));
      
      setPets(petsWithQueuePosition);
    } catch (error) {
      console.error('Failed to fetch pets:', error);
    } finally {
      setLoading(false);
    }
  };


  // Pull-to-Refresh function
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchPets(), fetchStats()]);
      setLastUpdated(new Date());
      showToast('✅ Dashboard refreshed!', 'success');
    } catch (error) {
      console.error('Refresh error:', error);
      showToast('❌ Failed to refresh', 'error');
    } finally{
      setIsRefreshing(false);
    }

  };

  // Touch-based pull-to-refresh for iPhone home screen apps (PWA standalone mode)
  const [pullDistance, setPullDistance] = React.useState(0);
  const [isPulling, setIsPulling] = React.useState(false);
  const touchStartY = React.useRef(0);

  React.useEffect(() => {
    let startY = 0;
    let currentY = 0;

    const handleTouchStart = (e) => {
      // Only activate if at top of page
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        touchStartY.current = startY;
        setIsPulling(true);
      }
    };

    const handleTouchMove = (e) => {
      if (!isPulling || window.scrollY > 0) {
        setIsPulling(false);
        setPullDistance(0);
        return;
      }

      currentY = e.touches[0].clientY;
      const distance = currentY - startY;

      // Only track downward pulls
      if (distance > 0 && distance <= 100) {
        setPullDistance(distance);
        // Prevent default scroll when pulling
        if (distance > 10) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = () => {
      if (isPulling && pullDistance > 50) {
        // Trigger refresh
        handleRefresh();
      }
      setIsPulling(false);
      setPullDistance(0);
    };

    // Add listeners to main element
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.addEventListener('touchstart', handleTouchStart, { passive: true });
      mainElement.addEventListener('touchmove', handleTouchMove, { passive: false });
      mainElement.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      if (mainElement) {
        mainElement.removeEventListener('touchstart', handleTouchStart);
        mainElement.removeEventListener('touchmove', handleTouchMove);
        mainElement.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isPulling, pullDistance]);

  // WebSocket setup for real-time updates
  useEffect(() => {
    // Check if WebSocket was previously disabled due to failures
    const wsDisabled = localStorage.getItem('ws_disabled');
    if (wsDisabled === 'true') {
      console.log('ℹ️ WebSocket disabled (previous connection failures). Using manual refresh only.');
      console.log('ℹ️ To re-enable WebSocket, clear browser cache or localStorage');
      return;
    }
    
    let retryCount = 0;
    const maxRetries = 3;  // Stop after 3 failed attempts
    
    // Connect to WebSocket
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;  // Use current host (includes domain and port)
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/updates`;
    
    console.log('🔌 Attempting WebSocket connection:', wsUrl);
    
    const connectWebSocket = () => {
      if (retryCount >= maxRetries) {
        console.warn('⚠️ WebSocket unavailable. Dashboard will use manual refresh only.');
        console.log('ℹ️ Click 🔄 Refresh button or pull down to update dashboard');
        // Remember that WebSocket is disabled to prevent retry on page refresh
        localStorage.setItem('ws_disabled', 'true');
        return;
      }
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected - Real-time updates enabled');
        retryCount = 0;  // Reset retry count on successful connection
        // Clear disabled flag on successful connection
        localStorage.removeItem('ws_disabled');
        
        // Send ping every 30 seconds to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
        
        ws.pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('📨 WebSocket update received:', message.type);
          
          if (message.type === 'pet_updated' || message.type === 'pet_created' || message.type === 'pet_deleted') {
            // Refresh dashboard when pet data changes
            console.log('🔄 Auto-refreshing dashboard...');
            fetchPets();
            fetchStats();
            setLastUpdated(new Date());
          }
        } catch (error) {
          console.error('WebSocket message parsing error:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.warn('⚠️ WebSocket connection error (will retry)');
      };
      
      ws.onclose = (event) => {
        console.log('❌ WebSocket disconnected');
        if (ws.pingInterval) {
          clearInterval(ws.pingInterval);
        }
        
        retryCount++;
        
        if (retryCount < maxRetries) {
          // Exponential backoff: 5s, 10s, 15s
          const delay = 5000 * retryCount;
          console.log(`🔄 Reconnecting WebSocket in ${delay/1000}s... (attempt ${retryCount}/${maxRetries})`);
          setTimeout(() => {
            connectWebSocket();
          }, delay);
        } else {
          console.warn('⚠️ WebSocket disabled after 3 failed attempts');
          console.log('ℹ️ Dashboard will use manual refresh only. Click 🔄 Refresh button to update.');
        }
      };
    };
    
    connectWebSocket();
    
    // Cleanup on unmount
    return () => {
      if (wsRef.current) {
        if (wsRef.current.pingInterval) {
          clearInterval(wsRef.current.pingInterval);
        }
        wsRef.current.close();
      }
    };
  }, []); // Empty dependency array - connect once on mount


  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending in our care SMS': return 'bg-red-100 text-red-800 border-red-200';
      case 'Awaiting Cremation SMS': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Awaiting Tree Planting Email': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Awaiting Pickup Ashes SMS': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Awaiting ashes collection booking': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'Awaiting Certificate Email': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Awaiting Review SMS': return 'bg-white text-purple-800 border-purple-300';
      case 'Completed': return 'bg-green-100 text-green-800 border-green-200'; // Only green when truly completed (review SMS sent)
      // Legacy statuses
      case 'In Progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Awaiting Keepsake': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Ready for Pickup': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const isNewWeek = (currentPet, previousPet) => {
    if (!previousPet) return false;
    
    // Helper function to get Monday of the week (standard calendar week: Monday-Sunday)
    const getMonday = (dateStr) => {
      const date = new Date(dateStr);
      const day = date.getDay(); // Sunday = 0, Monday = 1
      const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust Sunday to previous week's Monday
      return new Date(date.setDate(diff));
    };
    
    const currentMonday = getMonday(currentPet.arrival_date);
    const previousMonday = getMonday(previousPet.arrival_date);
    
    // Show "New Week" separator when pets are in different Monday-Sunday weeks
    return currentMonday.toDateString() !== previousMonday.toDateString();
  };

  const getPaymentStatusColor = (isPaid, refunded) => {
    if (refunded) return 'bg-red-100 text-red-800 border-red-300';
    return isPaid 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-red-100 text-red-800 border-red-200';
  };
  const getPaymentLabel = (pet) => {
    if (pet.refunded) return '↩ Refunded';
    return pet.is_paid ? '✅ Paid' : '⏳ Pending';
  };

  const clearFilters = () => {
    setFilters({ status: '', location: '', search: '', paid: '', prepayment: '' });
    sessionStorage.removeItem('petFilters');
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status }));
  };

  const handleStatCardClick = (status) => {
    // Handle special case for Prepayment filter
    if (status === 'Prepayment') {
      setFilters(prev => ({ ...prev, paid: prev.paid === false ? '' : false }));
    } else {
      setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status }));
    }
  };

  const handleLocationFilter = (location) => {
    setFilters(prev => ({ ...prev, location: prev.location === location ? '' : location }));
  };

  const handleDeletePet = async (pet) => {
    console.log('🗑️ Individual delete button clicked for:', pet.pet_name);
    
    // Use simple confirm instead of prompt for mobile compatibility
    const warningMessage = `🚨 PERMANENT DELETION WARNING 🚨\n\nYou are about to permanently delete:\nClient: ${pet.client_name}\nPet: ${pet.pet_name}\nDate: ${pet.arrival_date}\n\n⚠️ This action CANNOT be undone!\n\nAre you absolutely sure you want to proceed?`;
    
    const confirmed = window.confirm(warningMessage);

    if (!confirmed) {
      console.log('❌ Delete cancelled by user');
      return;
    }

    try {
      console.log('🔄 Attempting to delete pet:', pet.id);
      
      const response = await apiClient.delete(`/pets/${pet.id}`);
      
      console.log('✅ Delete response:', response.status);
      
      // Show success message
      alert(`✅ Record deleted successfully!\n\nClient: ${pet.client_name}\nPet: ${pet.pet_name}\n\nThe record has been permanently removed from the system.`);
      
      // Refresh the data
      fetchStats();
      fetchPets();
    } catch (error) {
      console.error('❌ Failed to delete pet record:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        alert('❌ Record not found. It may have already been deleted.');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        alert('❌ Authentication error. Please refresh the page and try again.');
      } else {
        alert('❌ Failed to delete record. Please try again.');
      }
      
      // Refresh data anyway in case the delete actually worked
      fetchStats();
      fetchPets();
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPets.size === 0) {
      alert('Please select at least one record to delete.');
      return;
    }

    // Show the bulk delete modal instead of using browser prompt
    setShowBulkDeleteModal(true);
    setBulkDeleteConfirmText('');
  };

  const executeBulkDelete = async () => {
    const selectedRecords = pets.filter(pet => selectedPets.has(pet.id));
    
    setShowBulkDeleteModal(false);
    setBulkDeleteConfirmText('');

    let successCount = 0;
    let failureCount = 0;
    const failures = [];

    // Show loading state
    showToast(`Starting deletion of ${selectedPets.size} records...`, 'info');

    try {
      // Delete each record with proper delay and error handling
      for (let i = 0; i < selectedRecords.length; i++) {
        const pet = selectedRecords[i];
        
        try {
          console.log(`Deleting pet ${i + 1}/${selectedRecords.length}: ${pet.client_name} - ${pet.pet_name}`);
          
          // Make the delete request
          const response = await apiClient.delete(`/pets/${pet.id}`);
          
          if (response.status === 200) {
            successCount++;
            console.log(`✅ Successfully deleted: ${pet.client_name} - ${pet.pet_name}`);
          } else {
            throw new Error(`Unexpected response status: ${response.status}`);
          }
          
        } catch (error) {
          failureCount++;
          failures.push(`${pet.client_name} - ${pet.pet_name}`);
          console.error(`❌ Failed to delete ${pet.client_name} - ${pet.pet_name}:`, error);
        }
        
        // Add small delay between deletions to prevent overwhelming the server
        if (i < selectedRecords.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Show final results
      if (failureCount === 0) {
        showToast(`✅ Successfully deleted all ${successCount} record(s)!`);
      } else if (successCount > 0) {
        showToast(`⚠️ Partially completed: ${successCount} deleted, ${failureCount} failed. Check console for details.`, 'error');
      } else {
        showToast(`❌ Bulk deletion failed. No records were deleted. Check console for details.`, 'error');
      }

      // Log summary for debugging
      console.log(`Bulk delete completed: ${successCount} successful, ${failureCount} failed`);
      if (failures.length > 0) {
        console.log('Failed deletions:', failures);
      }

    } catch (error) {
      console.error('Bulk delete process failed:', error);
      showToast('❌ Bulk deletion process failed. Check console for details.', 'error');
    }

    // Clear selection and refresh data
    setSelectedPets(new Set());
    
    // Add delay before refreshing to ensure all deletions are processed
    setTimeout(() => {
      fetchStats();
      fetchPets();
    }, 500);
  };

  // Tab state for Active vs Completed vs Prospects pets
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'completed', 'prospects'
  // Prospect states: when toggling in/out of AddPetModal for prospect flows
  const [prospectPrefill, setProspectPrefill] = useState(null);  // Data used to seed modal (from activate or edit)
  const [prospectEditingId, setProspectEditingId] = useState(null);  // If set, modal saves back to /prospects/{id}
  const [prospectStartMode, setProspectStartMode] = useState(false);  // Toggle default state
  const [prospectRefreshTrigger, setProspectRefreshTrigger] = useState(null);
  // Live count for the Prospects tab badge (active = new + follow_up)
  const [prospectActiveCount, setProspectActiveCount] = useState(0);

  // Fetch prospect active count for the tab badge
  const fetchProspectCount = useCallback(async () => {
    try {
      const res = await apiClient.get('/prospects/stats/summary');
      const s = res.data || {};
      setProspectActiveCount((s.new || 0) + (s.follow_up || 0));
    } catch (e) {
      // Silent fail — badge just shows 0
    }
  }, []);

  useEffect(() => {
    fetchProspectCount();
    // Refresh every 60s in case other staff add prospects
    const interval = setInterval(fetchProspectCount, 60000);
    return () => clearInterval(interval);
  }, [fetchProspectCount]);
  
  // Helper: find returning customer's other pets.
  // Prefer the backend-computed `prior_pets` (full DB + normalized phone match)
  // so it works in production regardless of dashboard pagination or phone
  // formatting. Falls back to client-side search of loaded data if absent.
  const getReturningPets = (pet) => {
    if (Array.isArray(pet.prior_pets)) return pet.prior_pets;
    if (!pet.phone_number && !pet.email) return [];
    const placeholderEmails = ['not@given', 'no@email', 'noemail@', 'none@', 'na@', 'n/a@', 'notprovided@'];
    const emailIsPlaceholder = pet.email && placeholderEmails.some(p => pet.email.toLowerCase().startsWith(p));
    return pets.filter(p => 
      p.id !== pet.id && (
        (pet.phone_number && p.phone_number === pet.phone_number) ||
        (pet.email && !emailIsPlaceholder && p.email === pet.email)
      )
    );
  };

  // First filter by tab (active vs completed)
  const tabFilteredPets = pets.filter(pet => {
    if (activeTab === 'active') {
      return pet.status !== 'Completed';
    } else if (activeTab === 'completed') {
      return pet.status === 'Completed';
    }
    return true;
  });
  
  // Then apply other filters
  const filteredPets = tabFilteredPets.filter(pet => {
    // Status filter
    if (filters.status && filters.status !== pet.status) {
      return false;
    }
    
    // Location filter
    if (filters.location && filters.location !== pet.location_area) {
      return false;
    }
    
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const searchableText = [
        pet.client_name,
        pet.pet_name,
        pet.phone_number,
        pet.email,
        pet.microchip_number,
        pet.breed,
        pet.vet_clinic,
      ].filter(Boolean).join(' ').toLowerCase();
      // Smart phone search — digits-only matching across formats (+61, 61, 04, missing 0)
      let phoneMatch = false;
      const searchDigits = filters.search.replace(/\D/g, '');
      if (searchDigits.length >= 3 && pet.phone_number) {
        const petDigits = pet.phone_number.replace(/\D/g, '');
        const variants = [searchDigits];
        if (searchDigits.startsWith('61') && searchDigits.length > 4) variants.push('0' + searchDigits.slice(2));
        else if ('23478'.includes(searchDigits[0])) variants.push('0' + searchDigits);
        phoneMatch = variants.some(v => petDigits.includes(v));
      }
      if (!searchableText.includes(searchTerm) && !phoneMatch) {
        return false;
      }
    }
    
    // Payment filter
    if (filters.paid !== '') {
      if (filters.paid !== pet.is_paid) {
        return false;
      }
    }
    
    // Prepayment filter
    if (filters.prepayment) {
      switch (filters.prepayment) {
        case 'due_soon':
          if (!isPrepaymentApproaching(pet.prepayment_date) || pet.pet_in_care_sms) {
            return false;
          }
          break;
        case 'set':
          // Check BOTH prepayment boolean AND prepayment_date fields
          if (!pet.prepayment && !pet.prepayment_date) {
            return false;
          }
          break;
        case 'none':
          // No prepayment if BOTH fields are false/empty
          if (pet.prepayment || pet.prepayment_date) {
            return false;
          }
          break;
      }
    }
    
    return true;
  });

  const handleSelectAll = () => {
    if (selectedPets.size === filteredPets.length) {
      setSelectedPets(new Set());
    } else {
      setSelectedPets(new Set(filteredPets.map(pet => pet.id)));
    }
  };

  const handleSelectPet = (petId) => {
    const newSelected = new Set(selectedPets);
    if (newSelected.has(petId)) {
      newSelected.delete(petId);
    } else {
      newSelected.add(petId);
    }
    setSelectedPets(newSelected);
  };

  const statusCounts = stats || {};
  const statusOptions = [
    { key: 'all', label: 'All', count: statusCounts.total_pets },
    { key: 'Pending in our care SMS', label: 'Pending in our care SMS', count: statusCounts.pending_care_sms },
    { key: 'Awaiting Cremation SMS', label: 'Awaiting Cremation SMS', count: statusCounts.awaiting_aquacremation_sms },
    { key: 'Awaiting Tree Planting Email', label: 'Awaiting Tree Planting Email', count: statusCounts.awaiting_tree_planting_email },
    { key: 'Awaiting Pickup Ashes SMS', label: 'Awaiting Pickup Ashes SMS', count: statusCounts.awaiting_pickup_ashes_sms },
    { key: 'Awaiting ashes collection booking', label: 'Awaiting ashes collection booking', count: statusCounts.awaiting_ashes_collection },
    { key: 'Awaiting Certificate Email', label: 'Awaiting Certificate Email', count: statusCounts.awaiting_certificate_email },
    { key: 'Awaiting Review SMS', label: 'Awaiting Review Request', count: statusCounts.awaiting_review_sms },
    { key: 'Completed', label: 'Completed', count: statusCounts.completed }
  ];

  const locationOptions = [
    { key: 'all', label: 'All Locations', count: statusCounts.total_pets },
    { key: 'Not Yet in Our Care', label: 'Not Yet in Our Care', count: statusCounts.locations?.['Not Yet in Our Care'] },
    { key: 'Refrigeration', label: 'Refrigeration', count: statusCounts.locations?.['Refrigeration'] },
    { key: 'Cremation Chambers', label: 'Cremation Chambers', count: statusCounts.locations?.['Cremation Chambers'] },
    { key: 'Cooling / Ashes Processing', label: 'Cooling / Ashes Processing', count: statusCounts.locations?.['Cooling / Ashes Processing'] },  // Added: New location step
    { key: 'Urn Assembly', label: 'Urn Assembled', count: statusCounts.locations?.['Urn Assembled'] },
    { key: 'Ready for Collection', label: 'Ready for Collection', count: statusCounts.locations?.['Ready for Collection'] }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="mx-auto px-6">
          {/* Desktop: Full width, Mobile: Contained */}
          <div className="flex flex-wrap justify-between items-center gap-3 py-4">
            <div className="flex items-center">
              <div className="h-10 w-10 flex items-center justify-center mr-3">
                <img 
                  src="https://cdn.signaturehound.com/users/6ye5ponkqg39anj/1f6lmi6zopy8.png" 
                  alt="HOLONS" 
                  className="h-8 w-8 object-contain"
                />
              </div>
              <h1 className="text-2xl font-bold" style={{ color: '#666666' }}>HOLONS</h1>
            </div>
            <div className="dashboard-header-actions flex flex-wrap min-w-0 max-w-full items-center gap-3" data-testid="dashboard-header-actions">
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`px-3 py-1.5 rounded text-xs ${isRefreshing ? 'bg-gray-200 text-gray-400' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}
                title="Refresh dashboard data"
                data-testid="dashboard-refresh-button"
              >
                {isRefreshing ? '⟳ Refreshing...' : '🔄 Refresh'}
              </button>
              
              <button
                onClick={async () => {
                  try {
                    showToast('Syncing WooCommerce orders...');
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/woocommerce/sync`, { method: 'POST' });
                    if (res.ok) {
                      const d = await res.json();
                      showToast(`WooCommerce sync complete — ${d.processed || 0} new orders`);
                      fetchPets();
                    } else { showToast('Sync failed', 'error'); }
                  } catch { showToast('Sync failed', 'error'); }
                }}
                className="hidden sm:block px-3 py-1.5 rounded text-xs bg-orange-100 text-orange-600 hover:bg-orange-200"
                title="Manually sync WooCommerce orders"
                data-testid="sync-woocommerce-btn"
              >
                Sync WooCommerce
              </button>
              
              <button
                onClick={() => setShowAnalytics(true)}
                data-testid="dashboard-analytics-button"
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition duration-200"
              >
                📊 Analytics
              </button>
              <LogoutButton />
            </div>
          </div>
        </div>
      </header>
      
      {/* Pull-to-Refresh Loading Indicator - Shows when pulling down */}
      {(isPulling || isRefreshing) && (
        <div 
          className="fixed top-16 left-0 right-0 flex justify-center z-50 transition-all duration-200"
          style={{ 
            transform: `translateY(${Math.min(pullDistance, 60)}px)`,
            opacity: pullDistance > 20 ? 1 : pullDistance / 20
          }}
        >
          <div className="bg-blue-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-2">
            {isRefreshing ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span className="text-sm font-medium">Refreshing...</span>
              </>
            ) : pullDistance > 50 ? (
              <>
                <span className="text-sm font-medium">Release to refresh</span>
              </>
            ) : (
              <>
                <span className="text-sm font-medium">Pull down to refresh</span>
              </>
            )}
          </div>
        </div>
      )}
      
      <main className="mx-auto px-6 py-8">
        {/* Desktop: Full width, Mobile: Contained */}
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {/* Current Week Pets - Click to clear all filters */}
            <button 
              onClick={() => setFilters({ status: '', location: '', search: '', paid: '', prepayment: '' })}
              className={`bg-white p-4 md:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 text-left ${
                !filters.status && !filters.location && !filters.paid ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="h-4 w-4 md:h-6 md:w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-3 md:ml-4">
                  <p className="text-xs md:text-sm font-medium text-gray-600">
                    {(() => {
                      // Calculate current week (newest week)
                      if (pets.length === 0) return 'No Pets';
                      
                      const newestDate = pets.reduce((latest, pet) => {
                        const petDate = new Date(pet.arrival_date);
                        return petDate > latest ? petDate : latest;
                      }, new Date(pets[0].arrival_date));
                      
                      const weekOfMonth = getWeekOfMonth(newestDate);
                      const monthName = newestDate.toLocaleDateString('en-US', { month: 'short' });
                      
                      return `Week ${weekOfMonth} ${monthName}`;
                    })()}
                  </p>
                  <p className="text-lg md:text-2xl font-semibold text-gray-900">
                    {(() => {
                      // Count pets in current week only
                      if (pets.length === 0) return 0;
                      
                      const newestDate = pets.reduce((latest, pet) => {
                        const petDate = new Date(pet.arrival_date);
                        return petDate > latest ? petDate : latest;
                      }, new Date(pets[0].arrival_date));
                      
                      const currentWeekPets = pets.filter(pet => {
                        const petDate = new Date(pet.arrival_date);
                        const newestDateObj = new Date(newestDate);
                        
                        // Use Monday-Sunday week calculation (standard calendar week)
                        const getMonday = (date) => {
                          const d = new Date(date);
                          const day = d.getDay(); // Sunday = 0, Monday = 1
                          const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust Sunday to previous week's Monday
                          return new Date(d.setDate(diff));
                        };
                        
                        const petMonday = getMonday(petDate);
                        const newestMonday = getMonday(newestDateObj);
                        
                        // Same calendar week if they have the same Monday
                        return petMonday.toDateString() === newestMonday.toDateString();
                      });
                      
                      return currentWeekPets.length;
                    })()}
                  </p>
                </div>
              </div>
            </button>

            {/* Pending in our care SMS */}
            <button 
              onClick={() => handleStatCardClick('Pending in our care SMS')}
              className={`bg-white p-4 md:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 text-left ${
                filters.status === 'Pending in our care SMS' ? 'ring-2 ring-red-500 bg-red-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <svg className="h-4 w-4 md:h-6 md:w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 md:ml-4">
                  <p className="text-xs md:text-sm font-medium text-gray-600">Pending in our care SMS</p>
                  <p className="text-lg md:text-2xl font-semibold text-gray-900">{stats.pending_care_sms || 0}</p>
                </div>
              </div>
            </button>

            {/* Prepayment - RESTORED FOR STAFF QUICK ACCESS */}
            <button 
              onClick={() => setFilters(prev => ({ ...prev, prepayment: 'set' }))}
              className={`bg-white p-4 md:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 text-left ${
                filters.prepayment === 'set' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="h-4 w-4 md:h-6 md:w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div className="ml-3 md:ml-4">
                  <p className="text-xs md:text-sm font-medium text-gray-600">Prepayment</p>
                  <p className="text-lg md:text-2xl font-semibold text-gray-900">
                    {tabFilteredPets.filter(pet => pet.prepayment || pet.prepayment_date).length}
                  </p>
                </div>
              </div>
            </button>

            {/* Awaiting Payment */}
            <button 
              onClick={() => {
                console.log('🔄 Awaiting Payment filter clicked');
                setFilters(prev => ({ ...prev, status: '', location: '', search: '', paid: prev.paid === false ? '' : false, prepayment: '' }));
              }}
              className={`bg-white p-4 md:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 text-left ${
                filters.paid === false ? 'ring-2 ring-orange-500 bg-orange-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <svg className="h-4 w-4 md:h-6 md:w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3 md:ml-4">
                  <p className="text-xs md:text-sm font-medium text-gray-600">Awaiting Payment</p>
                  <p className="text-lg md:text-2xl font-semibold text-gray-900">{stats.unpaid || 0}</p>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Filters - Collapsible on Mobile */}
        <div className="bg-white rounded-xl shadow-sm">
          {/* Filter Toggle Button - Mobile Only */}
          <div className="lg:hidden px-4 py-3 border-b border-gray-200">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="text-sm font-medium text-gray-900">
                Filters {(filters.status || filters.location || filters.search) && '(Active)'}
              </span>
              <svg
                className={`h-5 w-5 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Filter Content */}
          <div className={`p-4 md:p-6 ${showFilters ? 'block' : 'hidden md:block'}`}>
            
            {/* Mobile-Optimized Filters Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              
              {/* Status Filter Pills */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Workflow Status</h4>
                <div className="grid grid-cols-2 gap-1">
                  {statusOptions.map(({ key, label, count }) => (
                    <button
                      key={key}
                      onClick={() => handleStatusFilter(key === 'all' ? '' : key)}
                      className={`px-1.5 py-1.5 rounded text-xs font-medium transition-colors overflow-hidden ${
                        (key === 'all' && filters.status === '') || filters.status === key
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title={`${label} (${count || 0})`}
                    >
                      <div className="truncate">{label} ({count || 0})</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Filter Pills */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">🏷️ On-site Location & Activity</h4>
                <div className="grid grid-cols-2 gap-1">
                  {locationOptions.map(({ key, label, count }) => (
                    <button
                      key={key}
                      onClick={() => handleLocationFilter(key === 'all' ? '' : key)}
                      className={`px-1.5 py-1.5 rounded text-xs font-medium transition-colors overflow-hidden ${
                        (key === 'all' && filters.location === '') || filters.location === key
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title={`${label} (${count || 0})`}
                    >
                      <div className="truncate">{label} ({count || 0})</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Prepayment Filter Pills */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Prepayments</h4>
                <div className="grid grid-cols-2 gap-1">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, prepayment: '' }))}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                      filters.prepayment === ''
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All ({tabFilteredPets.length})
                  </button>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, prepayment: 'due_soon' }))}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                      filters.prepayment === 'due_soon'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Due Soon ({tabFilteredPets.filter(pet => isPrepaymentApproaching(pet.prepayment_date)).length})
                  </button>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, prepayment: 'set' }))}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                      filters.prepayment === 'set'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    With Prepayment ({tabFilteredPets.filter(pet => pet.prepayment || pet.prepayment_date).length})
                  </button>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, prepayment: 'none' }))}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                      filters.prepayment === 'none'
                        ? 'bg-gray-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    No Prepayment ({tabFilteredPets.filter(pet => !pet.prepayment && !pet.prepayment_date).length})
                  </button>
                </div>
              </div>
              
            </div>

          {/* Search and Controls */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by client, pet name, email, or vet clinic..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                {(filters.status || filters.location || filters.search || filters.paid) && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 md:gap-3">
              <button
                onClick={() => setShowQRScanner(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition duration-200 text-sm md:text-base"
              >
                📷 Scan QR
              </button>
              <button
                onClick={() => { setOnlineSalesTab('orders'); setShowOnlineSales(true); }}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition duration-200 text-sm md:text-base"
                data-testid="online-sales-btn"
              >
                🛒 Sales
              </button>
              <button
                onClick={() => setShowWalkInForm(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition duration-200 text-sm md:text-base"
              >
                📝 Walk-in
              </button>
              <button
                onClick={() => setShowClinicRewards(true)}
                className="bg-teal-600 hover:bg-teal-700 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition duration-200 text-sm md:text-base"
              >
                🏥 Rewards
              </button>
              <button
                onClick={() => setShowPet400(true)}
                className="bg-blue-700 hover:bg-blue-800 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition duration-200 text-sm md:text-base"
              >
                🔥 Cremators
              </button>
              <button
                onClick={() => setShowCalendar(true)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition duration-200 text-sm md:text-base"
              >
                📅 Calendar
              </button>
              <button
                onClick={() => setShowInventory(true)}
                className="bg-slate-700 hover:bg-slate-800 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition duration-200 text-sm md:text-base"
                data-testid="inventory-btn"
              >
                📋 Inventory
              </button>
              <button
                onClick={() => setShowPricing(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition duration-200 text-sm md:text-base"
                data-testid="pricing-btn"
              >
                💲 Pricing
              </button>
              <button
                onClick={() => setShowNotificationSettings(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition duration-200 text-sm md:text-base"
                data-testid="notification-settings-btn"
              >
                🔔 Alerts
              </button>
              <button
                onClick={() => setShowAddForm(true)}
                data-testid="add-new-pet-button"
                className="hidden md:block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition duration-200"
              >
                Add New Pet
              </button>
            </div>
          </div>
          </div>
        </div>

        {/* Mobile Search Bar and Add Pet Button - ALWAYS VISIBLE */}
        <div className="lg:hidden space-y-4 mb-6">
          {/* Mobile Search Bar - Permanent on top */}
          <div className="bg-white rounded-lg shadow-sm p-3 border">
            <input
              type="text"
              placeholder="Search clients, pets, vet clinics..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Mobile Add New Pet Button - Permanent under search */}
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => setShowCalendar(true)}
              className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-3 rounded-lg font-medium transition duration-200 flex items-center space-x-2 text-sm"
            >
              <span className="text-xl">📅</span>
              <span>Calendar</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition duration-200 flex items-center space-x-2 text-sm"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add New Pet</span>
            </button>
          </div>
        </div>

        {/* Active vs Completed Tabs - PHYSICAL SEPARATION */}
        <div className="mb-6 bg-white rounded-xl shadow-sm">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('active')}
              data-testid="tab-active"
              className={`flex-1 px-4 py-3 text-sm font-medium text-center rounded-tl-xl ${
                activeTab === 'active'
                  ? 'bg-blue-600 text-white border-b-2 border-blue-600'
                  : 'bg-gray-50 text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              🔴 Active Pets ({pets.filter(p => p.status !== 'Completed').length})
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`flex-1 px-4 py-3 text-sm font-medium text-center ${
                activeTab === 'completed'
                  ? 'bg-green-600 text-white border-b-2 border-green-600'
                  : 'bg-gray-50 text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
              data-testid="tab-completed"
            >
              🟢 Completed Pets ({pets.filter(p => p.status === 'Completed').length})
            </button>
            <button
              onClick={() => setActiveTab('prospects')}
              className={`flex-1 px-4 py-3 text-sm font-medium text-center rounded-tr-xl ${
                activeTab === 'prospects'
                  ? 'bg-amber-600 text-white border-b-2 border-amber-600'
                  : 'bg-gray-50 text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
              data-testid="tab-prospects"
            >
              🟡 Prospects
              {prospectActiveCount > 0 && (
                <span className={`ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 text-[11px] font-bold rounded-full ${
                  activeTab === 'prospects'
                    ? 'bg-white text-amber-700'
                    : 'bg-amber-100 text-amber-800'
                }`} data-testid="prospect-tab-count">
                  {prospectActiveCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Prospects view replaces the pet list when active */}
        {activeTab === 'prospects' ? (
          <ProspectsView
            vetClinics={[]}
            onCreate={(refresh) => {
              // Open AddPetModal in prospect-create mode (toggle ON, no prefill)
              setProspectPrefill(null);
              setProspectEditingId(null);
              setProspectStartMode(true);
              setProspectRefreshTrigger(() => () => { refresh(); fetchProspectCount(); });
              setShowAddForm(true);
            }}
            onEdit={(prospect, refresh) => {
              // Open AddPetModal in prospect-edit mode (toggle ON, prefilled from prospect data)
              // Convert prospect-only field names to match AddPetModal's expectations
              const prefill = {
                ...prospect,
                // Map prospect notes → AddPetModal's appointment_notes
                appointment_notes: prospect.appointment_notes || prospect.notes || '',
                // Map quoted_total → total_cost for form seeding
                total_cost: prospect.quoted_total,
                // Ensure sex default (AddPetModal expects M/F, not null)
                sex: prospect.sex || '',
              };
              setProspectPrefill(prefill);
              setProspectEditingId(prospect.id);
              setProspectStartMode(true);
              setProspectRefreshTrigger(() => () => { refresh(); fetchProspectCount(); });
              setShowAddForm(true);
            }}
            onActivate={(prefill, refresh) => {
              // Activation: prefill the form WITHOUT prospectId so it creates a new pet record.
              // from_prospect_id is in prefill → AddPetModal will call link-converted-pet after POST /pets.
              setProspectPrefill(prefill);
              setProspectEditingId(null);
              setProspectStartMode(false);  // Toggle OFF = save as pet
              setProspectRefreshTrigger(() => () => { refresh(); fetchProspectCount(); });
              setShowAddForm(true);
            }}
            onCountChange={fetchProspectCount}
          />
        ) : (<></>)}

        {/* Mobile-First Responsive Table */}
        <div className={activeTab === 'prospects' ? 'hidden' : 'block'}>
          {pets.length === 0 && !loading ? (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">
                <svg className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No pet records found</h3>
              <p className="text-gray-500">
                {Object.keys(filters).some(key => filters[key] !== '') ? 
                  "Try adjusting your filters or search terms." : 
                  "Get started by adding your first pet record."
                }
              </p>
            </div>
          ) : loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading pet records...</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                <div className="bg-white rounded-xl shadow-sm">
                  {/* Bulk Delete Controls */}
                  {selectedPets.size > 0 && (
                    <div className="bg-red-50 border-b border-red-200 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-red-800 font-medium">
                            {selectedPets.size} record(s) selected
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => setSelectedPets(new Set())}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Clear Selection
                          </button>
                          <button
                            onClick={handleBulkDelete}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium text-sm"
                          >
                            🗑️ Delete Selected
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="w-full min-w-0">
                    <table className="dashboard-pet-table w-full table-fixed divide-y divide-gray-200" data-testid="dashboard-pet-table">
                      <colgroup>
                        {[4, 4, 19, 10, 12, 8, 10, 17, 12, 4].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}
                      </colgroup>
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">Q#</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Day</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">Pet & Client</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Location</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Status</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Payment</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">Vet Clinic</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">Collection</th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                            <input
                              type="checkbox"
                              checked={pets.length > 0 && selectedPets.size === pets.length}
                              onChange={handleSelectAll}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredPets.map((pet, index) => {
                          const previousPet = index > 0 ? filteredPets[index - 1] : null;
                          const isWeekBoundary = isNewWeek(pet, previousPet);
                          
                          return (
                            <React.Fragment key={pet.id}>
                              {isWeekBoundary && (
                                <tr className="bg-blue-50 border-t-2 border-blue-200">
                                  <td className="px-2 py-2 text-center bg-blue-50"></td>
                                  <td className="px-3 py-2 bg-blue-50"></td>
                                  <td colSpan="8" className="px-6 py-2 text-center bg-blue-50">
                                    <div className="flex items-center justify-center">
                                      <div className="flex-1 h-px bg-blue-200"></div>
                                      <span className="px-4 text-sm font-medium text-blue-700">
                                        📅 New Week: {formatDate(pet.arrival_date)}
                                      </span>
                                      <div className="flex-1 h-px bg-blue-200"></div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              <tr 
                                className={`cursor-pointer ${getRowAlert(pet)} ${
                                  pet.status === 'Completed' 
                                    ? 'bg-green-100 hover:bg-green-200 border-green-300' 
                                    : `${getWeekColor(null, pet.arrival_date, pet.pet_name)} hover:opacity-75`
                                } ${isWeekBoundary ? 'border-t-2 border-blue-200' : ''} ${
                                  pet.pet_collected_by_driver && !pet.pet_in_care_sms ? 'neon-teal-glow' : ''
                                }`}
                                style={!pet.is_paid ? {
                                  boxShadow: '0 0 15px rgba(249, 115, 22, 0.5), 0 0 30px rgba(249, 115, 22, 0.3)'
                                } : pet.pet_collected_by_driver && !pet.pet_in_care_sms ? {
                                  boxShadow: '0 0 15px rgba(0, 220, 240, 0.6), 0 0 30px rgba(0, 160, 176, 0.4)',
                                  animation: 'neon-pulse 2s ease-in-out infinite'
                                } : {}}
                                onClick={() => navigate(`/pets/${pet.id}`)}
                              >
                                {/* Q# Column */}
                                <td className="px-2 py-4 whitespace-nowrap text-center w-12">
                                  <div className="flex flex-col items-center justify-center relative gap-1">
                                    <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-xs font-bold ${
                                      pet.status === 'Completed' 
                                        ? 'bg-green-100 border-green-400 text-green-800' 
                                        : getWeekColor(null, pet.arrival_date, pet.pet_name)
                                    }`}>
                                      #{pet.weekly_pet_number || '?'}
                                    </span>
                                    
                                    {/* URGENT Badge - Below Q# number */}
                                    {pet.urgent && (
                                      <div className="bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow-md border border-white animate-pulse whitespace-nowrap">
                                        ⚠️ URGENT
                                      </div>
                                    )}
                                    
                                    {/* Corner Badge for Unpaid Pets - Desktop (inside Q# cell) */}
                                    {!pet.is_paid && (
                                      <div className="absolute -top-2 -left-1 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10 animate-bounce">
                                        <span className="text-white text-xs">💰</span>
                                      </div>
                                    )}
                                    {/* Star Badge for Prepayments - Desktop */}
                                    {pet.prepayment && !pet.pet_in_care_sms && (
                                      <div className="absolute -top-2 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10">
                                        <span className="text-white text-xs">⭐️</span>
                                      </div>
                                    )}
                                    {/* Ready for Cremation - Desktop (bottom-right of Q# cell) */}
                                    {isReadyBadgeVisible(pet) && (
                                      <div
                                        className="absolute -bottom-2 -right-1 w-6 h-6 bg-cyan-400 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10 animate-pulse"
                                        title="Ready for Cremation"
                                        data-testid={`ready-badge-desktop-${pet.id}`}
                                      >
                                        <span className="text-white text-xs">🔥</span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                
                                {/* Day Column */}
                                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 w-20">
                                  <div className="font-bold text-blue-600">{getDayOfWeek(pet.arrival_date)}</div>
                                </td>
                                
                                {/* Pet & Client Column */}
                                <td className="px-3 py-4 whitespace-nowrap w-64">
                                  <div className="flex items-center">
                                    <div className="ml-4">
                                      <div className="font-medium text-gray-900 flex items-center space-x-2" style={{ textTransform: 'uppercase' }}>
                                        <span>{pet.pet_name}</span>
                                        
                                        {/* Gender badge for desktop - matches mobile implementation */}
                                        <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-xs font-bold text-white ${
                                          pet.sex === 'M' ? 'bg-blue-500' : 'bg-pink-500'
                                        }`}>
                                          {pet.sex}
                                        </span>
                                        
                                        {/* Show single notes indicator when notes exist - desktop */}
                                        {((pet.jewelry_notes && pet.jewelry_notes.trim()) || (pet.keepsakes_notes && pet.keepsakes_notes.trim())) && (
                                          <span 
                                            data-testid={`pet-notes-indicator-${pet.id}`}
                                            className="text-blue-600 text-sm cursor-pointer hover:text-blue-800 px-1 py-0.5 rounded hover:bg-blue-50" 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const note = [pet.jewelry_notes?.trim(), pet.keepsakes_notes?.trim()].filter(Boolean).join('\n');
                                              const formattedNote = formatNotesWithBullets(note);
                                              showToast(`💬 Notes:\n${formattedNote}`, 'info');
                                            }}
                                            title="Click to view notes"
                                          >
                                            💬
                                          </span>
                                        )}
                                        {/* Appointment Bell Icon - Desktop View */}
                                        {hasAppointments(pet) && (
                                          <button 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const summary = getAppointmentSummary(pet);
                                              showToast(summary, 'info');
                                            }}
                                            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-100 text-orange-600 border border-orange-300 hover:bg-orange-200 hover:border-orange-400 transition-colors cursor-pointer ml-1" 
                                            title="Click to view appointments"
                                          >
                                            🔔
                                          </button>
                                        )}
                                      </div>
                                      <div className="text-sm text-gray-500 truncate" style={{ textTransform: 'capitalize' }}>{pet.client_name}</div>
                                      {pet.breed && (
                                        <div className="text-xs text-gray-600 italic">{pet.breed} • {pet.weight_kg}kg</div>
                                      )}
                                      {pet.phone_number && (
                                        <div className="text-xs text-gray-400">{pet.phone_number}</div>
                                      )}
                                      {(() => {
                                        const priorPets = getReturningPets(pet);
                                        if (priorPets.length === 0) return null;
                                        return (
                                          <div className="text-xs text-blue-600 font-medium mt-0.5" title={priorPets.map(p => p.pet_name).join(', ')}>
                                            🔄 Prior Pet{priorPets.length > 1 ? 's' : ''}: {priorPets.map(p => `${p.pet_name}${p.breed ? ` (${p.breed})` : ''}${p.tree_planting_option ? ` 🌳${p.tree_planting_option}` : ''}`).join(', ')}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    {/* Total cost display - Prioritize manual price over calculated */}
                                    <div className="ml-2 shrink-0">
                                      <span data-testid={`dashboard-total-${pet.id}`} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                        ${pet.total_cost || calculateTotalCost(pet.pet_type, pet.weight_kg, 
                                          pet.urns_selected || [],
                                          pet.keepsakes_selected || [],
                                          pet.jewelry_selected || [],
                                          pet.transportation_fees,
                                          pet.certificate_type,
                                          pet.viewing_room
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                
                                {/* Location Column - MOVED UP for better priority visibility */}
                                <td className="px-3 py-4 whitespace-nowrap w-32">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getLocationChipColor(pet.location_area)}`}>
                                    {getLocationDisplay(pet.location_area, pet.location_detail)}
                                  </span>
                                </td>
                                
                                {/* Status Column - MOVED DOWN after location */}
                                <td className="px-3 py-4 whitespace-nowrap w-28">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(pet.status)}`}>
                                    {pet.status === 'Awaiting Review SMS' ? 'Awaiting Review Request' : pet.status}
                                  </span>
                                </td>
                                
                                {/* Payment Column */}
                                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 w-32">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPaymentStatusColor(pet.is_paid, pet.refunded)}`}>
                                    {getPaymentLabel(pet)}
                                  </span>
                                  {pet.client_paid && pet.clinic_payment_source && (
                                    <div className="mt-1">
                                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border border-amber-300 bg-amber-50 text-amber-800" data-testid="invoice-clinic-badge">
                                        💰 Invoice {pet.clinic_payment_source} — ${pet.amount_paid || 0}
                                      </span>
                                    </div>
                                  )}
                                  {getPaymentSummary(pet).isPartial && (
                                    <div className="mt-1 text-xs font-medium text-amber-700" data-testid={`partial-payment-desktop-${pet.id}`}>
                                      Paid ${getPaymentSummary(pet).totalPaid} of ${getPaymentSummary(pet).totalCost} — ${getPaymentSummary(pet).owing} owing
                                    </div>
                                  )}
                                </td>
                                
                                {/* Vet Clinic Column */}
                                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 w-48">
                                  <div className="truncate">{pet.vet_clinic || '-'}</div>
                                </td>
                                
                                {/* Collection Column */}
                                <td className="px-3 py-4 whitespace-nowrap text-xs w-36">
                                  {pet.ashes_collection_datetime && !pet.ashes_collection_completed && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCalendarInitialView('timeslot');
                                        setShowCalendar(true);
                                      }}
                                      className="bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5 hover:bg-blue-100 hover:border-blue-300 transition-colors cursor-pointer text-left block"
                                      title="Click to view in calendar"
                                    >
                                      <div className="text-blue-800 font-medium text-xs">
                                        🏺 Ashes Pick up: {new Date(pet.ashes_collection_datetime).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </div>
                                      <div className="text-blue-600 text-xs">
                                        {new Date(pet.ashes_collection_datetime).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </div>
                                      {pet.ashes_collection_notes && pet.ashes_collection_notes.trim() && (
                                        <div className="text-blue-700 text-xs mt-1 italic border-t border-blue-200 pt-1">
                                          💬 {pet.ashes_collection_notes}
                                        </div>
                                      )}
                                    </button>
                                  )}
                                  {pet.prepayment && pet.prepayment_date && !pet.prepayment_completed && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCalendarInitialView('timeslot');
                                        setShowCalendar(true);
                                      }}
                                      className="bg-orange-50 border border-orange-200 rounded-lg px-2 py-1.5 mt-1 hover:bg-orange-100 hover:border-orange-300 transition-colors cursor-pointer text-left block"
                                      title="Click to view in calendar"
                                    >
                                      <div className="text-orange-800 font-medium text-xs">
                                        ⭐️ Prepayment: {new Date(pet.prepayment_date).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </div>
                                      <div className="text-orange-600 text-xs">
                                        {new Date(pet.prepayment_date).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </div>
                                      {pet.prepayment_notes && pet.prepayment_notes.trim() && (
                                        <div className="text-orange-700 text-xs mt-1 italic border-t border-orange-200 pt-1">
                                          💬 {pet.prepayment_notes}
                                        </div>
                                      )}
                                    </button>
                                  )}
                                  {pet.viewing_room_datetime && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCalendarInitialView('timeslot');
                                        setShowCalendar(true);
                                      }}
                                      className="bg-purple-50 border border-purple-200 rounded-lg px-2 py-1.5 mt-1 hover:bg-purple-100 hover:border-purple-300 transition-colors cursor-pointer text-left block"
                                      title="Click to view in calendar"
                                    >
                                      <div className="text-purple-800 font-medium text-xs">
                                        🕯️ Viewing: {new Date(pet.viewing_room_datetime).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </div>
                                      <div className="text-purple-600 text-xs">
                                        {new Date(pet.viewing_room_datetime).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </div>
                                      {pet.viewing_room_notes && pet.viewing_room_notes.trim() && (
                                        <div className="text-purple-700 text-xs mt-1 italic border-t border-purple-200 pt-1">
                                          💬 {pet.viewing_room_notes}
                                        </div>
                                      )}
                                    </button>
                                  )}
                                  {pet.showroom_visit_datetime && !pet.showroom_visit_completed && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCalendarInitialView('timeslot');
                                        setShowCalendar(true);
                                      }}
                                      className="bg-teal-50 border border-teal-200 rounded-lg px-2 py-1.5 mt-1 hover:bg-teal-100 hover:border-teal-300 transition-colors cursor-pointer text-left block"
                                      title="Click to view in calendar"
                                    >
                                      <div className="text-teal-800 font-medium text-xs">
                                        🚙 Showroom: {new Date(pet.showroom_visit_datetime).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </div>
                                      <div className="text-teal-600 text-xs">
                                        {new Date(pet.showroom_visit_datetime).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </div>
                                      {pet.showroom_visit_notes && pet.showroom_visit_notes.trim() && (
                                        <div className="text-teal-700 text-xs mt-1 italic border-t border-teal-200 pt-1">
                                          💬 {pet.showroom_visit_notes}
                                        </div>
                                      )}
                                    </button>
                                  )}
                                  {pet.other_appointment_datetime && !pet.other_appointment_completed && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCalendarInitialView('timeslot');
                                        setShowCalendar(true);
                                      }}
                                      className="bg-pink-50 border border-pink-200 rounded-lg px-2 py-1.5 mt-1 hover:bg-pink-100 hover:border-pink-300 transition-colors cursor-pointer text-left block"
                                      title="Click to view in calendar"
                                    >
                                      <div className="text-pink-800 font-medium text-xs">
                                        🚐 Vet Collection: {new Date(pet.other_appointment_datetime).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </div>
                                      <div className="text-pink-600 text-xs">
                                        {new Date(pet.other_appointment_datetime).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </div>
                                      {pet.other_appointment_notes && pet.other_appointment_notes.trim() && (
                                        <div className="text-pink-700 text-xs mt-1 italic border-t border-pink-200 pt-1">
                                          💬 {pet.other_appointment_notes}
                                        </div>
                                      )}
                                    </button>
                                  )}
                                  {pet.pet_delivery_datetime && !pet.pet_delivery_completed && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCalendarInitialView('timeslot');
                                        setShowCalendar(true);
                                      }}
                                      className="bg-green-50 border border-green-200 rounded-lg px-2 py-1.5 mt-1 hover:bg-green-100 hover:border-green-300 transition-colors cursor-pointer text-left block"
                                      title="Click to view in calendar"
                                    >
                                      <div className="text-green-800 font-medium text-xs">
                                        🚚 Ashes Delivery: {new Date(pet.pet_delivery_datetime).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </div>
                                      <div className="text-green-600 text-xs">
                                        {new Date(pet.pet_delivery_datetime).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </div>
                                      {pet.pet_delivery_notes && pet.pet_delivery_notes.trim() && (
                                        <div className="text-green-700 text-xs mt-1 italic border-t border-green-200 pt-1">
                                          💬 {pet.pet_delivery_notes}
                                        </div>
                                      )}
                                    </button>
                                  )}
                                  {pet.pet_dropoff_datetime && !pet.pet_dropoff_completed && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCalendarInitialView('timeslot');
                                        setShowCalendar(true);
                                      }}
                                      className="bg-indigo-50 border border-indigo-200 rounded-lg px-2 py-1.5 mt-1 hover:bg-indigo-100 hover:border-indigo-300 transition-colors cursor-pointer text-left block"
                                      title="Click to view in calendar"
                                    >
                                      <div className="text-indigo-800 font-medium text-xs">
                                        🦮 Client Drop off: {new Date(pet.pet_dropoff_datetime).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </div>
                                      <div className="text-indigo-600 text-xs">
                                        {new Date(pet.pet_dropoff_datetime).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </div>
                                      {pet.pet_dropoff_notes && pet.pet_dropoff_notes.trim() && (
                                        <div className="text-indigo-700 text-xs mt-1 italic border-t border-indigo-200 pt-1">
                                          💬 {pet.pet_dropoff_notes}
                                        </div>
                                      )}
                                    </button>
                                  )}
                                  {pet.home_collection_datetime && !pet.home_collection_completed && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCalendarInitialView('timeslot');
                                        setShowCalendar(true);
                                      }}
                                      className="bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5 mt-1 hover:bg-emerald-100 hover:border-emerald-300 transition-colors cursor-pointer text-left block"
                                      title="Click to view in calendar"
                                    >
                                      <div className="text-emerald-800 font-medium text-xs">
                                        🏡 Home Collection: {new Date(pet.home_collection_datetime).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </div>
                                      <div className="text-emerald-600 text-xs">
                                        {new Date(pet.home_collection_datetime).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </div>
                                      {pet.home_collection_notes && pet.home_collection_notes.trim() && (
                                        <div className="text-emerald-700 text-xs mt-1 italic border-t border-emerald-200 pt-1">
                                          💬 {pet.home_collection_notes}
                                        </div>
                                      )}
                                    </button>
                                  )}
                                  {pet.vet_dropoff_datetime && !pet.vet_dropoff_completed && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCalendarInitialView('timeslot');
                                        setShowCalendar(true);
                                      }}
                                      className="bg-orange-50 border border-orange-200 rounded-lg px-2 py-1.5 mt-1 hover:bg-orange-100 hover:border-orange-300 transition-colors cursor-pointer text-left block"
                                      title="Click to view in calendar"
                                    >
                                      <div className="text-orange-800 font-medium text-xs">
                                        🏥 Vet Drop off: {new Date(pet.vet_dropoff_datetime).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </div>
                                      <div className="text-orange-600 text-xs">
                                        {new Date(pet.vet_dropoff_datetime).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </div>
                                      {pet.vet_dropoff_notes && pet.vet_dropoff_notes.trim() && (
                                        <div className="text-orange-700 text-xs mt-1 italic border-t border-orange-200 pt-1">
                                          💬 {pet.vet_dropoff_notes}
                                        </div>
                                      )}
                                    </button>
                                  )}
                                  {!pet.ashes_collection_datetime && !pet.prepayment_date && !pet.viewing_room_datetime && !pet.showroom_visit_datetime && !pet.other_appointment_datetime && !pet.pet_delivery_datetime && !pet.pet_dropoff_datetime && !pet.home_collection_datetime && !pet.vet_dropoff_datetime && (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                
                                {/* Actions Column */}
                                <td className="px-3 py-4 whitespace-nowrap text-center w-24">
                                  <div className="flex flex-wrap items-center justify-center gap-1">
                                    {/* Quick Action Buttons - Desktop Version */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowPaymentModal(pet);
                                      }}
                                      className="text-green-600 hover:text-green-900 text-xs px-1.5 py-1 rounded border border-green-300 hover:bg-green-50"
                                      title="Quick Payment Update"
                                    >
                                      💰
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowLocationModal(pet);
                                      }}
                                      className="text-blue-600 hover:text-blue-900 text-xs px-1.5 py-1 rounded border border-blue-300 hover:bg-blue-50"
                                      title="Quick Location Update"
                                    >
                                      📍
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowTransportationModal(pet);
                                      }}
                                      className="text-purple-600 hover:text-purple-900 text-xs px-1.5 py-1 rounded border border-purple-300 hover:bg-purple-50"
                                      title="Quick Transport Update"
                                    >
                                      🚚
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setWorkflowPet(pet);
                                      }}
                                      className="text-orange-600 hover:text-orange-900 text-xs px-1.5 py-1 rounded border border-orange-300 hover:bg-orange-50"
                                      title="Quick SMS/Workflow Update"
                                      data-testid={`dashboard-workflow-desktop-${pet.id}`}
                                    >
                                      📧
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowQuickNotesModal(pet);
                                      }}
                                      className="text-purple-600 hover:text-purple-900 text-xs px-1.5 py-1 rounded border border-purple-300 hover:bg-purple-50"
                                      title="Quick Notes"
                                    >
                                      💬
                                    </button>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        console.log('🏷️ QR Label button clicked for pet:', pet.pet_name, pet.id);
                                        
                                        try {
                                          // Fetch the PDF
                                          const url = `${process.env.REACT_APP_BACKEND_URL}/api/pets/${pet.id}/qr-label-pdf`;
                                          console.log('Fetching QR label from:', url);
                                          
                                          const response = await fetch(url);
                                          console.log('Response status:', response.status, response.statusText);
                                          
                                          if (!response.ok) {
                                            throw new Error(`Failed to generate QR label: ${response.status} ${response.statusText}`);
                                          }
                                          
                                          // Create blob from response
                                          const blob = await response.blob();
                                          console.log('Blob created, size:', blob.size, 'type:', blob.type);
                                          
                                          // Try multiple download methods for better browser compatibility
                                          try {
                                            // Method 1: Programmatic download (works on most browsers)
                                            const blobUrl = window.URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = blobUrl;
                                            link.download = `${pet.pet_name}_QR_Label.pdf`;
                                            link.style.display = 'none';
                                            document.body.appendChild(link);
                                            
                                            // Force click event
                                            link.click();
                                            
                                            // Cleanup after download starts
                                            setTimeout(() => {
                                              document.body.removeChild(link);
                                              window.URL.revokeObjectURL(blobUrl);
                                            }, 500);
                                            
                                            console.log('✅ Download triggered via link click');
                                          } catch (downloadError) {
                                            console.warn('Link click failed, trying alternative method:', downloadError);
                                            
                                            // Method 2: Open in new window (fallback for strict browsers)
                                            const blobUrl = window.URL.createObjectURL(blob);
                                            const newWindow = window.open(blobUrl, '_blank');
                                            
                                            if (!newWindow) {
                                              throw new Error('Download blocked. Please allow popups for this site.');
                                            }
                                            
                                            console.log('✅ Download triggered via window.open');
                                            
                                            setTimeout(() => {
                                              window.URL.revokeObjectURL(blobUrl);
                                            }, 1000);
                                          }
                                          
                                          showToast('✅ QR Label downloaded successfully!');
                                          console.log('✅ QR Label download complete');
                                        } catch (error) {
                                          console.error('❌ Error downloading QR label:', error);
                                          showToast(`❌ Failed to download QR label: ${error.message}`, 'error');
                                        }
                                      }}
                                      className="text-teal-600 hover:text-teal-900 text-xs px-1.5 py-1 rounded border border-teal-300 hover:bg-teal-50"
                                      title="Print QR Label"
                                    >
                                      🏷️
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/pets/${pet.id}`);
                                      }}
                                      className="text-indigo-600 hover:text-indigo-900 text-xs px-1.5 py-1 rounded border border-indigo-300 hover:bg-indigo-50"
                                      title="View Details"
                                    >
                                      👁️
                                    </button>
                                  </div>
                                </td>
                                
                                {/* Checkbox Column */}
                                <td 
                                  className="px-3 py-4 whitespace-nowrap text-center w-16"
                                  onClick={(e) => e.stopPropagation()} // Prevent row navigation when clicking checkbox area
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedPets.has(pet.id)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleSelectPet(pet.id);
                                    }}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Mobile Card View - NO HORIZONTAL SCROLLING */}
              <div className="lg:hidden space-y-4">
                {/* Mobile Bulk Delete Controls */}
                {selectedPets.size > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-red-800 font-medium">
                        {selectedPets.size} record(s) selected
                      </span>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setSelectedPets(new Set())}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Clear
                        </button>
                        <button
                          onClick={handleBulkDelete}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg font-medium text-sm"
                        >
                          🗑️ Delete Selected
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {filteredPets.map((pet, index) => {
                  const previousPet = index > 0 ? filteredPets[index - 1] : null;
                  const isWeekBoundary = isNewWeek(pet, previousPet);
                  const isExpanded = expandedCards.has(pet.id);
                  
                  // Calculate week of month from arrival date for color coding
                  const weekOfMonth = getWeekOfMonth(pet.arrival_date);
                  // Use green only when status is "Completed" (which means review SMS has been sent)
                  const isCompleted = pet.status === 'Completed';
                  
                  console.log(`🐛 DEBUG Pet ${pet.pet_name} (${pet.client_name}): status="${pet.status}", isCompleted=${isCompleted}, review_sms=${pet.review_sms}`);
                  
                  // Proper logic: Green background only when status is "Completed"
                  const weekColor = isCompleted 
                    ? 'bg-green-100 border-green-400 text-green-800' 
                    : getWeekColor(weekOfMonth, pet.arrival_date, pet.pet_name);
                  
                  return (
                    <React.Fragment key={pet.id}>
                      {isWeekBoundary && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                          <div className="flex items-center justify-center">
                            <div className="flex-1 h-px bg-blue-200"></div>
                            <span className="px-4 text-sm font-medium text-blue-700">
                              📅 New Week: {formatDate(pet.arrival_date)}
                            </span>
                            <div className="flex-1 h-px bg-blue-200"></div>
                          </div>
                        </div>
                      )}
                      
                      <div className={`rounded-lg shadow-sm border p-3 relative ${weekColor} ${getRowAlert(pet)} ${
                        !pet.is_paid 
                          ? 'prepayment-glow' 
                          : ''
                      }`}>
                        {/* Neon Teal Animation - Pet Collected by Driver */}
                        {pet.pet_collected_by_driver && !pet.pet_in_care_sms && (
                          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
                            <defs>
                              <filter id="neon-glow-mobile">
                                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                                <feMerge>
                                  <feMergeNode in="coloredBlur"/>
                                  <feMergeNode in="SourceGraphic"/>
                                </feMerge>
                              </filter>
                              <filter id="neon-glow-strong-mobile">
                                <feGaussianBlur stdDeviation="6" result="coloredBlur"/>
                                <feMerge>
                                  <feMergeNode in="coloredBlur"/>
                                  <feMergeNode in="coloredBlur"/>
                                  <feMergeNode in="SourceGraphic"/>
                                </feMerge>
                              </filter>
                            </defs>
                            {/* Outer glow */}
                            <rect
                              x="2" y="2"
                              width="calc(100% - 4px)"
                              height="calc(100% - 4px)"
                              rx="8"
                              fill="none"
                              stroke="rgba(0, 160, 176, 0.8)"
                              strokeWidth="3"
                              strokeLinecap="round"
                              filter="url(#neon-glow-strong-mobile)"
                              strokeDasharray="110, 890"
                            >
                              <animate
                                attributeName="stroke-dashoffset"
                                from="1000"
                                to="-1000"
                                dur="20s"
                                repeatCount="indefinite"
                              />
                            </rect>
                            {/* Core line */}
                            <rect
                              x="2" y="2"
                              width="calc(100% - 4px)"
                              height="calc(100% - 4px)"
                              rx="8"
                              fill="none"
                              stroke="rgba(0, 220, 240, 1)"
                              strokeWidth="2"
                              strokeLinecap="round"
                              filter="url(#neon-glow-mobile)"
                              strokeDasharray="90, 910"
                            >
                              <animate
                                attributeName="stroke-dashoffset"
                                from="1000"
                                to="-1000"
                                dur="20s"
                                repeatCount="indefinite"
                              />
                            </rect>
                          </svg>
                        )}
                        
                        {/* URGENT Badge for Mobile - Top Center */}
                        {pet.urgent && (
                          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-20">
                            <div className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded shadow-lg border-2 border-white animate-pulse whitespace-nowrap">
                              ⚠️ URGENT
                            </div>
                          </div>
                        )}

                        {/* Vet Staff Benefit Badge */}
                        {pet.vet_staff_benefit && (
                          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 z-20">
                            <div className={`text-white text-xs font-bold px-2.5 py-0.5 rounded-full shadow border-2 border-white whitespace-nowrap ${pet.vet_staff_benefit === 'discount_10' ? 'bg-green-600' : 'bg-purple-600'}`}>
                              {pet.vet_staff_benefit === 'discount_10' ? '🏷️ Vet Staff' : '🏺 Comp. Urn'}
                            </div>
                          </div>
                        )}
                        
                        {/* Corner Badge for Unpaid Pets */}
                        {!pet.is_paid && (
                          <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10 animate-bounce">
                            <span className="text-white text-lg">💰</span>
                          </div>
                        )}
                        
                        {/* Corner Badge for Prepayments */}
                        {pet.prepayment && !pet.pet_in_care_sms && (
                          <div className="absolute -top-2 -left-2 w-7 h-7 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10">
                            <span className="text-white text-base">⭐️</span>
                          </div>
                        )}
                        
                        {/* Badge for Printed Certificates - REMOVED from top */}
                        {false && pet.certificate_type === 'Printed' && (
                          <div className="absolute top-2 right-2 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded border border-amber-300 shadow-sm z-10">
                            📄 Printed
                          </div>
                        )}
                        
                        {/* Collapsed View - Always Visible */}
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => toggleCardExpansion(pet.id)}
                          data-testid={`dashboard-pet-toggle-${pet.id}`}
                        >
                          <div className="flex-1 min-w-0 pr-3">
                            <div className="flex items-center space-x-2 mb-1">
                              {/* PET NAME PRIORITY: Give more space to pet name, truncate customer name if needed */}
                              <span className="font-semibold text-gray-700 text-lg" style={{ textTransform: 'uppercase' }}>{pet.pet_name}</span>
                              
                              {/* Gender icon next to pet name for better staff usability */}
                              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold text-white ${
                                pet.sex === 'M' ? 'bg-blue-500' : 'bg-pink-500'
                              }`}>
                                {pet.sex}
                              </span>
                              
                              {/* Show single notes indicator when jewelry_notes exists */}
                              {pet.jewelry_notes && pet.jewelry_notes.trim() && (
                                <span 
                                  className="text-blue-600 text-sm cursor-pointer hover:text-blue-800 px-1 py-0.5 rounded hover:bg-blue-50" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const formattedNote = formatNotesWithBullets(pet.jewelry_notes);
                                    showToast(`💬 Notes:\n${formattedNote}`, 'info');
                                  }}
                                  title="Tap to view notes"
                                >
                                  💬
                                </span>
                              )}
                              
                              <span className="text-gray-500">-</span>
                              <span className="font-semibold text-gray-900 text-sm truncate max-w-32" style={{ textTransform: 'capitalize' }}>{pet.client_name}</span>
                              {/* Prepayment Alert Bell Icon */}
                              {isPrepaymentApproaching(pet.prepayment_date) && !pet.pet_in_care_sms && (
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('🔔 Bell clicked for pet:', pet.pet_name, 'prepayment_date:', pet.prepayment_date);
                                    const dueDate = new Date(pet.prepayment_date).toLocaleDateString('en-AU', { 
                                      weekday: 'long', 
                                      year: 'numeric', 
                                      month: 'long', 
                                      day: 'numeric' 
                                    });
                                    
                                    // Use showToast instead of alert for mobile compatibility
                                    showToast(`💰 Prepayment Due: ${dueDate} - Pet: ${pet.pet_name} (${pet.client_name})`, 'warning');
                                    console.log('🔔 Toast notification shown for prepayment due');
                                  }}
                                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-100 text-orange-600 border border-orange-300 hover:bg-orange-200 hover:border-orange-400 transition-colors cursor-pointer" 
                                  title={`Prepayment due: ${pet.prepayment_date}`}
                                >
                                  🔔
                                </button>
                              )}
                            </div>
                            
                            {/* Contact info - Mobile on top, Email below */}
                            <div className="text-sm text-gray-600 mt-1">
                              {pet.phone_number && (
                                <div className="font-medium flex items-center space-x-2">
                                  <span>📱 {pet.phone_number}</span>
                                  {pet.breed && (
                                    <span className="text-xs text-gray-600 italic">{pet.breed} • {pet.weight_kg}kg</span>
                                  )}
                                </div>
                              )}
                              {pet.email && (
                                <div className="font-medium">✉️ {pet.email}</div>
                              )}
                              {(() => {
                                const priorPets = getReturningPets(pet);
                                if (priorPets.length === 0) return null;
                                return (
                                  <div className="text-xs text-blue-600 font-medium mt-1" title={priorPets.map(p => p.pet_name).join(', ')}>
                                    🔄 Prior Pet{priorPets.length > 1 ? 's' : ''}: {priorPets.map(p => `${p.pet_name}${p.breed ? ` (${p.breed})` : ''}${p.tree_planting_option ? ` 🌳${p.tree_planting_option}` : ''}`).join(', ')}
                                  </div>
                                );
                              })()}
                            </div>
                            
                            {/* Appointment Dates - Mobile Collapsed View */}
                            {(pet.ashes_collection_datetime || pet.prepayment_date || pet.viewing_room_datetime || pet.showroom_visit_datetime || pet.other_appointment_datetime || pet.pet_delivery_datetime || pet.pet_dropoff_datetime || pet.home_collection_datetime || pet.vet_dropoff_datetime) && (
                              <div className="mt-2 space-y-1">
                                {pet.ashes_collection_datetime && !pet.ashes_collection_completed && (
                                  <div className="text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1 inline-block">
                                    <div className="font-medium text-blue-800">
                                      🏺 Ashes Pick up: {new Date(pet.ashes_collection_datetime).toLocaleDateString('en-AU', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })} at {new Date(pet.ashes_collection_datetime).toLocaleTimeString('en-AU', { 
                                        hour: 'numeric', 
                                        minute: '2-digit', 
                                        hour12: true 
                                      })}
                                    </div>
                                    {pet.ashes_collection_notes && pet.ashes_collection_notes.trim() && (
                                      <div className="text-blue-700 text-xs mt-1 italic border-t border-blue-200 pt-1">
                                        💬 {pet.ashes_collection_notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {pet.prepayment && pet.prepayment_date && !pet.prepayment_completed && (
                                  <div className="text-xs bg-orange-50 border border-orange-200 rounded px-2 py-1 inline-block ml-1">
                                    <div className="font-medium text-orange-800">
                                      ⭐️ Prepayment: {new Date(pet.prepayment_date).toLocaleDateString('en-AU', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })}
                                    </div>
                                    <div className="text-orange-600">
                                      {new Date(pet.prepayment_date).toLocaleTimeString('en-AU', { 
                                        hour: 'numeric', 
                                        minute: '2-digit', 
                                        hour12: true 
                                      })}
                                    </div>
                                    {pet.prepayment_notes && pet.prepayment_notes.trim() && (
                                      <div className="text-orange-700 text-xs mt-1 italic border-t border-orange-200 pt-1">
                                        💬 {pet.prepayment_notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {pet.viewing_room_datetime && (
                                  <div className="text-xs bg-purple-50 border border-purple-200 rounded px-2 py-1 inline-block ml-1">
                                    <div className="font-medium text-purple-800">
                                      🕯️ Viewing: {new Date(pet.viewing_room_datetime).toLocaleDateString('en-AU', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })} at {new Date(pet.viewing_room_datetime).toLocaleTimeString('en-AU', { 
                                        hour: 'numeric', 
                                        minute: '2-digit', 
                                        hour12: true 
                                      })}
                                    </div>
                                    {pet.viewing_room_notes && pet.viewing_room_notes.trim() && (
                                      <div className="text-purple-700 text-xs mt-1 italic border-t border-purple-200 pt-1">
                                        💬 {pet.viewing_room_notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {pet.showroom_visit_datetime && !pet.showroom_visit_completed && (
                                  <div className="text-xs bg-teal-50 border border-teal-200 rounded px-2 py-1 inline-block ml-1">
                                    <div className="font-medium text-teal-800">
                                      🚙 Showroom: {new Date(pet.showroom_visit_datetime).toLocaleDateString('en-AU', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })} at {new Date(pet.showroom_visit_datetime).toLocaleTimeString('en-AU', { 
                                        hour: 'numeric', 
                                        minute: '2-digit', 
                                        hour12: true 
                                      })}
                                    </div>
                                    {pet.showroom_visit_notes && pet.showroom_visit_notes.trim() && (
                                      <div className="text-teal-700 text-xs mt-1 italic border-t border-teal-200 pt-1">
                                        💬 {pet.showroom_visit_notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {pet.other_appointment_datetime && !pet.other_appointment_completed && (
                                  <div className="text-xs bg-pink-50 border border-pink-200 rounded px-2 py-1 inline-block ml-1">
                                    <div className="font-medium text-pink-800">
                                      🚐 Vet Collection: {new Date(pet.other_appointment_datetime).toLocaleDateString('en-AU', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })} at {new Date(pet.other_appointment_datetime).toLocaleTimeString('en-AU', { 
                                        hour: 'numeric', 
                                        minute: '2-digit', 
                                        hour12: true 
                                      })}
                                    </div>
                                    {pet.other_appointment_notes && pet.other_appointment_notes.trim() && (
                                      <div className="text-pink-700 text-xs mt-1 italic border-t border-pink-200 pt-1">
                                        💬 {pet.other_appointment_notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {pet.pet_delivery_datetime && !pet.pet_delivery_completed && (
                                  <div className="text-xs bg-green-50 border border-green-200 rounded px-2 py-1 inline-block ml-1">
                                    <div className="font-medium text-green-800">
                                      🚚 Ashes Delivery: {new Date(pet.pet_delivery_datetime).toLocaleDateString('en-AU', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })} at {new Date(pet.pet_delivery_datetime).toLocaleTimeString('en-AU', { 
                                        hour: 'numeric', 
                                        minute: '2-digit', 
                                        hour12: true 
                                      })}
                                    </div>
                                    {pet.pet_delivery_notes && pet.pet_delivery_notes.trim() && (
                                      <div className="text-green-700 text-xs mt-1 italic border-t border-green-200 pt-1">
                                        💬 {pet.pet_delivery_notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {pet.pet_dropoff_datetime && !pet.pet_dropoff_completed && (
                                  <div className="text-xs bg-indigo-50 border border-indigo-200 rounded px-2 py-1 inline-block ml-1">
                                    <div className="font-medium text-indigo-800">
                                      🦮 Client Drop off: {new Date(pet.pet_dropoff_datetime).toLocaleDateString('en-AU', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })} at {new Date(pet.pet_dropoff_datetime).toLocaleTimeString('en-AU', { 
                                        hour: 'numeric', 
                                        minute: '2-digit', 
                                        hour12: true 
                                      })}
                                    </div>
                                    {pet.pet_dropoff_notes && pet.pet_dropoff_notes.trim() && (
                                      <div className="text-indigo-700 text-xs mt-1 italic border-t border-indigo-200 pt-1">
                                        💬 {pet.pet_dropoff_notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {pet.home_collection_datetime && !pet.home_collection_completed && (
                                  <div className="text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-block ml-1">
                                    <div className="font-medium text-emerald-800">
                                      🏡 Home Collection: {new Date(pet.home_collection_datetime).toLocaleDateString('en-AU', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })} at {new Date(pet.home_collection_datetime).toLocaleTimeString('en-AU', { 
                                        hour: 'numeric', 
                                        minute: '2-digit', 
                                        hour12: true 
                                      })}
                                    </div>
                                    {pet.home_collection_notes && pet.home_collection_notes.trim() && (
                                      <div className="text-emerald-700 text-xs mt-1 italic border-t border-emerald-200 pt-1">
                                        💬 {pet.home_collection_notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {pet.vet_dropoff_datetime && !pet.vet_dropoff_completed && (
                                  <div className="text-xs bg-orange-50 border border-orange-200 rounded px-2 py-1 inline-block ml-1">
                                    <div className="font-medium text-orange-800">
                                      🏥 Vet Drop off: {new Date(pet.vet_dropoff_datetime).toLocaleDateString('en-AU', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })} at {new Date(pet.vet_dropoff_datetime).toLocaleTimeString('en-AU', { 
                                        hour: 'numeric', 
                                        minute: '2-digit', 
                                        hour12: true 
                                      })}
                                    </div>
                                    {pet.vet_dropoff_notes && pet.vet_dropoff_notes.trim() && (
                                      <div className="text-orange-700 text-xs mt-1 italic border-t border-orange-200 pt-1">
                                        💬 {pet.vet_dropoff_notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Mobile-friendly total cost display - Use manual price if available, otherwise calculated */}
                            <div className="flex flex-wrap items-center gap-1 mt-2 sm:hidden">
                              <span className="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full font-medium">
                                💵 Total: ${pet.total_cost || calculateTotalCost(pet.pet_type, pet.weight_kg, 
                                  pet.urns_selected || [],
                                  pet.keepsakes_selected || [],
                                  pet.jewelry_selected || [],
                                  pet.transportation_fees,
                                  pet.certificate_type,
                                  pet.viewing_room
                                )}
                              </span>
                              {pet.client_paid && pet.clinic_payment_source && (
                                <span className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-full">
                                  💰 Invoice {pet.clinic_payment_source} — ${pet.amount_paid || 0}
                                </span>
                              )}
                              {getPaymentSummary(pet).isPartial && (
                                <span className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-full" data-testid={`partial-payment-mobile-${pet.id}`}>
                                  💳 Paid ${getPaymentSummary(pet).totalPaid} of ${getPaymentSummary(pet).totalCost} — ${getPaymentSummary(pet).owing} owing
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Right Side - Day/Number Display + Desktop Cost + Checkbox */}
                          <div className="flex items-center space-x-2">
                            {/* 🔥 Ready for Cremation — small circle to the left of the #8 pill (same vertical height) */}
                            {isReadyBadgeVisible(pet) && (
                              <div
                                className="w-7 h-7 bg-cyan-400 rounded-full flex items-center justify-center shadow-lg border-2 border-white animate-pulse flex-shrink-0"
                                title="Ready for Cremation"
                                data-testid={`ready-badge-mobile-${pet.id}`}
                              >
                                <span className="text-white text-base leading-none">🔥</span>
                              </div>
                            )}
                            {/* Day, Number, and Pet Type - Right corner, stacked vertically on mobile */}
                            <div className="flex flex-col items-center space-y-0.5">
                              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md bg-blue-100 text-blue-800 text-xs font-bold border border-blue-300">
                                {getDayOfWeek(pet.arrival_date)}
                              </span>
                              <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-bold border ${
                                pet.status === 'Completed' 
                                  ? 'bg-green-100 text-green-800 border-green-300' 
                                  : getWeekColor(null, pet.arrival_date, pet.pet_name)
                              }`}>
                                #{pet.weekly_pet_number || '?'}
                              </span>
                              {/* Pet Type Indicator */}
                              <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-bold border ${
                                pet.pet_type === 'Cat' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                                pet.pet_type === 'Dog' ? 'bg-green-100 text-green-800 border-green-300' :
                                'bg-orange-100 text-orange-800 border-orange-300'
                              }`}>
                                {pet.pet_type === 'Exotic Pet' ? 'EXOTIC' : pet.pet_type.toUpperCase()}
                              </span>
                            </div>
                            
                            {/* Total Cost Display - Prioritize manual price over calculated */}
                            <div className="hidden sm:flex bg-blue-100 border border-blue-300 px-2 py-1 rounded-md">
                              <span className="text-blue-800 font-bold text-sm">
                                ${pet.total_cost || calculateTotalCost(pet.pet_type, pet.weight_kg, 
                                  pet.urns_selected || [],
                                  pet.keepsakes_selected || [],
                                  pet.jewelry_selected || [],
                                  pet.transportation_fees,
                                  pet.certificate_type,
                                  pet.viewing_room
                                )}
                              </span>
                            </div>
                            <input
                              type="checkbox"
                              checked={selectedPets.has(pet.id)}
                              onChange={(e) => {
                                e.stopPropagation(); // Prevent card expansion when clicking checkbox
                                handleSelectPet(pet.id);
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-gray-400 text-sm">
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          </div>
                        </div>

                        {/* Expanded View - Conditional */}
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-gray-200 border-opacity-50">
                            {/* Interactive Smart Display - Clickable icons for quick actions */}
                            <div className="space-y-2 text-sm mb-4">
                              
                              {/* Cremation Package - Show selected urns or default pouch */}
                              <div className="flex justify-between items-center">
                                <div className="font-medium text-xs text-right">
                                  {pet.urns_selected && pet.urns_selected.length > 0 ? (
                                    <div className="flex flex-wrap">
                                      {pet.urns_selected.map((urn, index) => (
                                        <span key={index} className="inline-flex items-center mr-2 mb-1">
                                          {(() => {
                                            console.log('🔍 MOBILE CARD DEBUG - Processing urn:', urn);
                                            console.log('🔍 MOBILE CARD DEBUG - Pet type:', pet.pet_type);
                                            const customIcon = getCustomProductIcon(urn, pet.pet_type);
                                            console.log('🔍 MOBILE CARD DEBUG - Custom icon result:', customIcon);
                                            return customIcon && customIcon.type === 'image' ? (
                                              <img 
                                                src={customIcon.src} 
                                                alt={customIcon.alt}
                                                className="w-4 h-4 mr-1 rounded object-contain border border-gray-300"
                                                style={{ minWidth: '16px', minHeight: '16px' }}
                                                onLoad={(e) => {
                                                  console.log('✅ MOBILE CARD - Custom icon loaded:', customIcon.src);
                                                }}
                                                onError={(e) => {
                                                  console.error('❌ MOBILE CARD - Custom icon failed:', customIcon.src);
                                                  e.target.style.display = 'none';
                                                  // Show fallback
                                                  const fallback = e.target.nextElementSibling;
                                                  if (fallback) {
                                                    fallback.style.display = 'inline';
                                                  }
                                                }}
                                              />
                                            ) : (
                                              <span className="mr-1" style={{ display: customIcon ? 'none' : 'inline' }}>
                                                {getProductIcon(urn)}
                                              </span>
                                            );
                                          })()}
                                          <span className="text-xs">{urn}</span>
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    // Default package based on pet sex
                                    <span className="text-blue-600">
                                      {pet.sex === 'F' ? '🩷 Pink Pouch + Candle' : '🔵 Blue Pouch + Candle'}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Keepsake Collection Status */}
                              {pet.keepsakes_selected && pet.keepsakes_selected.length > 0 && (() => {
                                const keepsakes = pet.keepsakes_collection || [];
                                const collected = keepsakes.filter(k => k.collected).length;
                                const total = pet.keepsakes_selected.length;
                                const allCollected = collected === total && total > 0;
                                const noneCollected = collected === 0;
                                
                                return (
                                  <div className="mb-2">
                                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                      allCollected ? 'bg-green-100 text-green-800 border border-green-300' : 
                                      noneCollected ? 'bg-gray-100 text-gray-600 border border-gray-300' : 
                                      'bg-amber-100 text-amber-800 border border-amber-300'
                                    }`}>
                                      {allCollected ? '✅' : noneCollected ? '☐' : '⚠️'} Keepsakes: {collected}/{total}
                                    </span>
                                  </div>
                                );
                              })()}

                              {/* Keepsakes - Only show if selected with visual icons */}
                              {pet.keepsakes_selected && pet.keepsakes_selected.length > 0 && (
                                <div className="flex justify-between items-center">
                                  <div className="font-medium text-xs text-right flex flex-wrap">
                                    {pet.keepsakes_selected.map((keepsake, index) => {
                                      console.log('🔍 Processing keepsake:', keepsake); // Debug exact name
                                      const customIcon = getCustomProductIcon(keepsake, pet.pet_type);
                                      console.log('🎯 Custom icon result:', customIcon); // Debug result
                                      return (
                                        <span key={index} className="inline-flex items-center mr-2 mb-1">
                                          {customIcon && customIcon.type === 'image' ? (
                                            <img 
                                              src={customIcon.src} 
                                              alt={customIcon.alt}
                                              className="w-5 h-5 mr-1 rounded object-contain border border-gray-300"
                                              style={{ 
                                                minWidth: '20px', 
                                                minHeight: '20px',
                                                maxWidth: '20px',
                                                maxHeight: '20px',
                                                backgroundColor: '#f8f9fa'
                                              }}
                                              onLoad={(e) => {
                                                console.log('✅ Custom icon LOADED successfully:', customIcon.src);
                                              }}
                                              onError={(e) => {
                                                console.error('❌ Custom icon FAILED to load:', customIcon.src);
                                                console.error('❌ Image error event:', e);
                                                e.target.style.display = 'none';
                                                // Show fallback emoji
                                                const fallback = e.target.nextElementSibling;
                                                if (fallback && fallback.classList.contains('emoji-fallback')) {
                                                  fallback.style.display = 'inline';
                                                }
                                              }}
                                            />
                                          ) : null}
                                          <span 
                                            className="emoji-fallback mr-1 text-base" 
                                            style={{ display: (customIcon && customIcon.type === 'image') ? 'none' : 'inline' }}
                                          >
                                            {getProductIcon(keepsake)}
                                          </span>
                                          <span className="text-xs">{keepsake}</span>
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Jewelry - Only show if selected with visual icons */}
                              {pet.jewelry_selected && pet.jewelry_selected.length > 0 && (
                                <div className="flex justify-between items-center">
                                  <div className="font-medium text-xs text-right flex flex-wrap">
                                    {pet.jewelry_selected.map((jewelry, index) => {
                                      console.log('🔍 Processing jewelry:', jewelry); // Debug exact name
                                      const customIcon = getCustomProductIcon(pet.jewelry_selected[index], pet.pet_type);
                                      console.log('🎯 Jewelry custom icon result:', customIcon); // Debug result
                                      return (
                                        <span key={index} className="inline-flex items-center mr-2 mb-1">
                                          {customIcon && customIcon.type === 'image' ? (
                                            <img 
                                              src={customIcon.src} 
                                              alt={customIcon.alt}
                                              className="w-5 h-5 mr-1 rounded object-contain border border-gray-300"
                                              style={{ 
                                                minWidth: '20px', 
                                                minHeight: '20px',
                                                maxWidth: '20px',
                                                maxHeight: '20px',
                                                backgroundColor: '#f8f9fa'
                                              }}
                                              onLoad={(e) => {
                                                console.log('✅ Jewelry custom icon LOADED:', customIcon.src);
                                              }}
                                              onError={(e) => {
                                                console.error('❌ Jewelry custom icon FAILED:', customIcon.src);
                                                e.target.style.display = 'none';
                                                const fallback = e.target.nextElementSibling;
                                                if (fallback && fallback.classList.contains('emoji-fallback')) {
                                                  fallback.style.display = 'inline';
                                                }
                                              }}
                                            />
                                          ) : null}
                                          <span 
                                            className="emoji-fallback mr-1 text-base" 
                                            style={{ display: (customIcon && customIcon.type === 'image') ? 'none' : 'inline' }}
                                          >
                                            {getProductIcon(jewelry)}
                                          </span>
                                          <span className="text-xs">{jewelry}</span>
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Notes Content - Only show jewelry notes in expanded view */}
                              {pet.jewelry_notes && pet.jewelry_notes.trim() && (
                                <div className="flex items-start bg-purple-50 p-2 rounded border-l-2 border-purple-300 my-1">
                                  <span className="text-purple-600 mr-2 text-sm">💬</span>
                                  <div className="font-medium text-xs text-left text-purple-700 flex-1 whitespace-pre-line">
                                    {formatNotesWithBullets(pet.jewelry_notes)}
                                  </div>
                                </div>
                              )}

                              {/* Vet Clinic - Only show if specified */}
                              {pet.vet_clinic && pet.vet_clinic !== 'Not specified' && (
                                <div className="flex justify-between items-center">
                                  <div className="font-medium text-xs text-right">
                                    🏥 {pet.vet_clinic}
                                  </div>
                                </div>
                              )}

                              {/* Transportation - Only show if not default */}
                              {pet.transportation_fees && 
                               pet.transportation_fees !== 'Vet pick‑up (included) $0' && 
                               (!Array.isArray(pet.transportation_fees) || 
                                !pet.transportation_fees.includes('Vet pick‑up (included) $0') || 
                                pet.transportation_fees.length > 1) && (
                                <div className="flex justify-between items-center">
                                  <div className="font-medium text-xs text-right">
                                    🚚 {Array.isArray(pet.transportation_fees) ? 
                                      pet.transportation_fees
                                        .filter(fee => fee !== 'Vet pick‑up (included) $0')
                                        .map(fee => fee.split('$')[0].trim())
                                        .join(', ') : 
                                      pet.transportation_fees.split('$')[0].trim()
                                    }
                                  </div>
                                </div>
                              )}



                              {/* Existing Display Items - Show selected values */}
                              {/* Ashes Collection datetime removed - already shown in collapsed view */}

                              {/* Cremation SMS Date - Only show if scheduled */}
                              {pet.aquacremation_sms_date && (
                                <div className="flex justify-between items-center">
                                  <div className="font-medium text-xs text-right text-blue-600">
                                    🔥 {formatDate(pet.aquacremation_sms_date)}
                                  </div>
                                </div>
                              )}
                              
                              {/* Printed Certificate Indicator */}
                              {pet.certificate_type === 'Printed' && (
                                <div className="flex justify-between items-center">
                                  <div className="font-medium text-xs text-right text-amber-700">
                                    📄 Printed Certificate
                                  </div>
                                </div>
                              )}

                              {/* Tree Planting Tribute - Only show if selected */}
                              {pet.tree_planting_option && (
                                <div className="flex justify-between items-center">
                                  <div className="font-medium text-xs text-right text-green-600">
                                    🌳 {pet.tree_planting_option}
                                  </div>
                                </div>
                              )}

                            </div>

                            {/* Microchip Number - If available */}
                            {pet.microchip_number && (
                              <div className="mb-3 px-3 py-2 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="flex items-center text-sm">
                                  <span className="text-purple-700 font-medium">🔢 Microchip:</span>
                                  <span className="ml-2 font-mono text-purple-900">{pet.microchip_number}</span>
                                </div>
                              </div>
                            )}

                            {/* Status and Location */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLocationChipColor(pet.location_area)}`}>
                                📍 {getLocationDisplay(pet.location_area, pet.location_detail)}
                              </span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pet.status)}`}>
                                🔄 {pet.status === 'Awaiting Review SMS' ? 'Awaiting Review Request' : pet.status}
                              </span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                pet.is_paid ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                              }`}>
                                💰 {pet.refunded ? 'Refunded' : pet.is_paid ? 'Paid' : 'Pending'}
                              </span>
                              {/* Total Cost Display in Expanded View - Prioritize manual price */}
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                                💵 ${pet.total_cost || calculateTotalCost(pet.pet_type, pet.weight_kg, 
                                  pet.urns_selected || [],
                                  pet.keepsakes_selected || [],
                                  pet.jewelry_selected || [],
                                  pet.transportation_fees,
                                  pet.certificate_type,
                                  pet.viewing_room
                                )}
                              </span>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-200 border-opacity-50">
                              
                              {/* Quick Action Links - wraps to multiple lines on narrow viewports so View/Edit/🗑 stay visible */}
                              <div className="flex flex-wrap items-center text-xs text-gray-500 gap-x-2 gap-y-0">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowPaymentModal(pet);
                                  }}
                                  className="underline hover:text-blue-600"
                                >
                                  💰 Pay
                                </button>
                                <span>•</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowLocationModal(pet);
                                  }}
                                  className="underline hover:text-blue-600"
                                >
                                  📍 Loc
                                </button>
                                <span>•</span>
                                {/* Transport button hidden - removed to prevent layout overlap */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setScannedPet(pet);
                                    setShowKeepsakesModal(true);
                                  }}
                                  className="underline hover:text-purple-600"
                                >
                                  🎁 Keepsakes
                                </button>
                                <span>•</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setWorkflowPet(pet);
                                  }}
                                  className="underline hover:text-blue-600"
                                  data-testid={`dashboard-workflow-mobile-expanded-${pet.id}`}
                                >
                                  📧 SMS
                                </button>
                                <span>•</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowQuickNotesModal(pet);
                                  }}
                                  className="underline hover:text-purple-600"
                                >
                                  💬 Note
                                </button>
                                {/* Ready for Cremation toggle — only when pet hasn't moved past chamber yet */}
                                {!POST_READY_LOCATIONS.has(pet.location_area) && pet.status !== 'Completed' && (
                                  <>
                                    <span>•</span>
                                    <button
                                      onClick={(e) => toggleReadyForCremation(pet, e)}
                                      className={`underline ${
                                        pet.ready_for_aquacremation
                                          ? 'text-cyan-600 font-semibold hover:text-cyan-700'
                                          : 'hover:text-cyan-600'
                                      }`}
                                      title={pet.ready_for_aquacremation ? 'Click to unmark — pet is no longer ready' : 'Click to mark this pet ready for cremation'}
                                      data-testid={`ready-toggle-${pet.id}`}
                                    >
                                      🔥 {pet.ready_for_aquacremation ? 'Ready' : 'Mark Ready'}
                                    </button>
                                  </>
                                )}
                                <span>•</span>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    console.log('🏷️ Mobile QR Label button clicked for pet:', pet.pet_name, pet.id);
                                    
                                    try {
                                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/pets/${pet.id}/qr-label-pdf`;
                                      console.log('Fetching QR label from:', url);
                                      
                                      const response = await fetch(url);
                                      console.log('Response status:', response.status, response.statusText);
                                      
                                      if (!response.ok) {
                                        throw new Error(`Failed to generate QR label: ${response.status} ${response.statusText}`);
                                      }
                                      
                                      const blob = await response.blob();
                                      console.log('Blob created, size:', blob.size, 'type:', blob.type);
                                      
                                      const blobUrl = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = blobUrl;
                                      link.download = `${pet.pet_name}_QR_Label.pdf`;
                                      document.body.appendChild(link);
                                      link.click();
                                      
                                      setTimeout(() => {
                                        document.body.removeChild(link);
                                        window.URL.revokeObjectURL(blobUrl);
                                      }, 100);
                                      
                                      showToast('✅ QR Label downloaded!');
                                      console.log('✅ QR Label download complete');
                                    } catch (error) {
                                      console.error('❌ Error downloading QR label:', error);
                                      showToast(`❌ Failed to download: ${error.message}`, 'error');
                                    }
                                  }}
                                  className="underline hover:text-teal-600"
                                >
                                  🏷️ QR
                                </button>
                              </div>

                              <div className="flex flex-wrap gap-1 flex-1 justify-end">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/pets/${pet.id}`);
                                  }}
                                  className="text-blue-600 hover:text-blue-900 text-xs px-2 py-1 rounded border border-blue-300 hover:bg-blue-50 flex-shrink-0"
                                >
                                  View
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('🔧 Mobile Edit button clicked for pet:', pet.id);
                                    // Temporary fix: Use direct navigation
                                    window.location.href = `/pets/${pet.id}/edit`;
                                  }}
                                  className="text-green-600 hover:text-green-900 text-xs px-2 py-1 rounded border border-green-300 hover:bg-green-50 flex-shrink-0"
                                >
                                  Edit
                                </button>
                              </div>
                              
                              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePet(pet);
                                  }}
                                  className="text-red-600 hover:text-red-900 text-xs px-1.5 py-1 rounded border border-red-300 hover:bg-red-50"
                                  title="Delete"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-red-600">🚨 BULK DELETE WARNING</h2>
              <button
                onClick={() => {
                  setShowBulkDeleteModal(false);
                  setBulkDeleteConfirmText('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                You are about to permanently delete <strong>{selectedPets.size} record(s)</strong>:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto mb-4">
                {pets.filter(pet => selectedPets.has(pet.id)).map(pet => (
                  <div key={pet.id} className="flex items-center space-x-2 py-1">
                    <span className="text-sm">• {pet.client_name} - {pet.pet_name}</span>
                    <span className="text-xs text-gray-500">({pet.status || 'No status'})</span>
                  </div>
                ))}
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-red-800 mb-2">📊 This will permanently delete ALL associated data:</h3>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• SMS/Email workflow histories</li>
                  <li>• Payment information</li>
                  <li>• Location histories</li>
                  <li>• Keepsake details</li>
                  <li>• All timestamps and audit trails</li>
                </ul>
                <p className="font-bold text-red-800 mt-3">⚠️ THIS ACTION CANNOT BE UNDONE!</p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkDeleteModal(false);
                  setBulkDeleteConfirmText('');
                }}
                className="px-6 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={executeBulkDelete}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
              >
                🗑️ Yes, Delete {selectedPets.size} Records
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show analytics view if analytics is open */}
      {showAnalytics && (
        <SimpleAnalyticsView onClose={() => setShowAnalytics(false)} />
      )}

      {/* Show clinic rewards view if clinic rewards is open */}
      {showClinicRewards && (
        <ClinicRewardsView onClose={() => setShowClinicRewards(false)} />
      )}

      {/* Show add form if form is open */}
      {showAddForm && (
        <AddPetModal 
          editPet={prospectPrefill}
          prospectId={prospectEditingId}
          startAsProspect={prospectStartMode}
          onClose={() => {
            setShowAddForm(false);
            setProspectPrefill(null);
            setProspectEditingId(null);
            setProspectStartMode(false);
          }} 
          onSuccess={() => {
            setShowAddForm(false);
            const refresh = prospectRefreshTrigger;
            setProspectPrefill(null);
            setProspectEditingId(null);
            setProspectStartMode(false);
            setProspectRefreshTrigger(null);
            // Add small delay to allow backend to process manual price before refresh
            setTimeout(() => {
              fetchPets();
              fetchStats();
              if (refresh) refresh();
            }, 500);
            showToast('Saved successfully! ✅');
          }}
        />
      )}

      {/* QR Scanner Modal */}
      {showQRScanner && (
        <QRScannerModal 
          onClose={() => setShowQRScanner(false)}
          onPetScanned={(pet) => {
            setScannedPet(pet);
            setShowQRScanner(false);
          }}
        />
      )}

      {/* Quick Scan View Modal - Show pet info after scanning */}
      {scannedPet && (
        <QuickScanViewModal
          pet={scannedPet}
          onClose={() => setScannedPet(null)}
          onLocationUpdated={() => {
            fetchPets();
            fetchStats();
            setScannedPet(null);
            showToast('Location updated successfully! ✅');
          }}
          onKeepsakesClick={() => {
            setShowKeepsakesModal(true);
          }}
        />
      )}

      {/* Keepsakes Collection Modal */}
      {showKeepsakesModal && scannedPet && (
        <KeepsakesCollectionModal
          pet={scannedPet}
          onClose={() => {
            setShowKeepsakesModal(false);
            setScannedPet(null);
          }}
          onUpdate={() => {
            fetchPets();  // Refresh pet list
            fetchStats();  // Refresh stats
            // Refresh scanned pet data
            apiClient.get(`/pets/${scannedPet.id}`)
              .then(response => setScannedPet(response.data))
              .catch(err => console.error('Error refreshing pet data:', err));
          }}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Update Payment</h3>
              <button
                onClick={() => setShowPaymentModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pet: {showPaymentModal.pet_name} ({showPaymentModal.client_name})
                </label>
              </div>
              
              {/* Current Status */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600">Current Status</p>
                <p className={`font-bold ${showPaymentModal.refunded ? 'text-red-600' : showPaymentModal.is_paid ? 'text-green-600' : 'text-orange-600'}`}>
                  {showPaymentModal.refunded ? '↩ Refunded' : showPaymentModal.is_paid ? '✅ Paid' : '⏳ Pending'}
                </p>
                {showPaymentModal.payment_method && (
                  <p className="text-sm text-gray-600 mt-1">
                    Method: {showPaymentModal.payment_method}
                  </p>
                )}
              </div>

              {/* Payment Method Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                <select
                  id="payment-method-select"
                  defaultValue={showPaymentModal.payment_method || 'Online'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="Online">Online</option>
                  <option value="VetPay">VetPay</option>
                  <option value="EFTPOS">EFTPOS</option>
                  <option value="Cash">Cash</option>
                  <option value="Invoice">Invoice</option>
                  <option value="Afterpay">Afterpay</option>
                  <option value="Clinic Account">Clinic Account</option>
                </select>
              </div>

              {/* Payment Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      try {
                        const selectedMethod = document.getElementById('payment-method-select').value;
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pets/${showPaymentModal.id}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ 
                            is_paid: true, 
                            payment_method: selectedMethod,
                            payment_date: new Date().toISOString()
                          })
                        });
                        if (response.ok) {
                          await fetchPets();
                          showToast(`Payment received via ${selectedMethod} ✅`);
                          setShowPaymentModal(null);
                        }
                      } catch (error) {
                        showToast('Failed to update payment', 'error');
                      }
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
                  >
                    💰 Mark as Paid
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pets/${showPaymentModal.id}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ 
                            is_paid: false, 
                            payment_date: null 
                          })
                        });
                        if (response.ok) {
                          await fetchPets();
                          showToast('Payment marked as pending 💳');
                          setShowPaymentModal(null);
                        } else {
                          const errorData = await response.text();
                          console.error('Failed to mark as pending:', errorData);
                          showToast('Failed to update payment', 'error');
                        }
                      } catch (error) {
                        console.error('Payment update error:', error);
                        showToast('Failed to update payment', 'error');
                      }
                    }}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium"
                  >
                    💳 Mark as Pending
                  </button>
                </div>
              </div>

              {/* Partial Payment History */}
              {(() => {
                const summary = getPaymentSummary(showPaymentModal);
                return (
                  <div data-testid="payment-history-section">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment History</label>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Cost</span>
                        <span className="font-semibold text-gray-900" data-testid="payment-summary-total">${summary.totalCost}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Paid So Far</span>
                        <span className="font-semibold text-green-700" data-testid="payment-summary-paid">${summary.totalPaid}</span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                        <span className="text-gray-600">Balance Owing</span>
                        <span className={`font-bold ${summary.owing > 0 ? 'text-amber-700' : 'text-green-700'}`} data-testid="payment-summary-owing">${summary.owing}</span>
                      </div>
                    </div>

                    {/* Installment list */}
                    {summary.hasPayments && (
                      <ul className="mt-3 space-y-2" data-testid="payment-installment-list">
                        {summary.payments.map((p, idx) => (
                          <li key={p.id || idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm">
                            <div>
                              <span className="font-semibold text-gray-900">${parseFloat(p.amount || 0)}</span>
                              <span className="text-gray-500"> · {p.method || '—'}</span>
                              <div className="text-xs text-gray-400">{formatDate(p.date)}</div>
                            </div>
                            <button
                              data-testid={`delete-payment-${idx}`}
                              onClick={async () => {
                                try {
                                  const updatedPayments = summary.payments.filter((_, i) => i !== idx);
                                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pets/${showPaymentModal.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ payments: updatedPayments })
                                  });
                                  if (response.ok) {
                                    const data = await response.json();
                                    await fetchPets();
                                    setShowPaymentModal(data);
                                    showToast('Payment removed');
                                  } else {
                                    showToast('Failed to remove payment', 'error');
                                  }
                                } catch { showToast('Failed to remove payment', 'error'); }
                              }}
                              className="text-red-500 hover:text-red-700 text-xs font-medium"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Add installment form */}
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <input
                        id="add-payment-amount"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Amount"
                        data-testid="add-payment-amount"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <select
                        id="add-payment-method"
                        defaultValue={showPaymentModal.payment_method || 'Online'}
                        data-testid="add-payment-method"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="Online">Online</option>
                        <option value="VetPay">VetPay</option>
                        <option value="EFTPOS">EFTPOS</option>
                        <option value="Cash">Cash</option>
                        <option value="Invoice">Invoice</option>
                        <option value="Afterpay">Afterpay</option>
                        <option value="Clinic Account">Clinic Account</option>
                      </select>
                      <input
                        id="add-payment-date"
                        type="date"
                        defaultValue={new Date().toISOString().split('T')[0]}
                        data-testid="add-payment-date"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm col-span-2"
                      />
                    </div>
                    <button
                      data-testid="add-payment-btn"
                      onClick={async () => {
                        const amount = parseFloat(document.getElementById('add-payment-amount').value);
                        const method = document.getElementById('add-payment-method').value;
                        const dateVal = document.getElementById('add-payment-date').value;
                        if (!amount || amount <= 0) { showToast('Enter a valid amount', 'error'); return; }
                        try {
                          const newEntry = {
                            amount,
                            method,
                            date: dateVal ? new Date(dateVal + 'T00:00:00').toISOString() : new Date().toISOString()
                          };
                          const updatedPayments = [...summary.payments, newEntry];
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pets/${showPaymentModal.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ payments: updatedPayments })
                          });
                          if (response.ok) {
                            const data = await response.json();
                            await fetchPets();
                            setShowPaymentModal(data);
                            showToast(`Recorded payment of $${amount} ✅`);
                          } else {
                            showToast('Failed to record payment', 'error');
                          }
                        } catch { showToast('Failed to record payment', 'error'); }
                      }}
                      className="mt-2 w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm"
                    >
                      + Add Payment
                    </button>
                  </div>
                );
              })()}

              {/* Refund Section */}
              {showPaymentModal.is_paid && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Refund</label>
                  {showPaymentModal.refunded ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm font-semibold text-red-700">Refunded — ${showPaymentModal.refund_amount || 0}</p>
                      {showPaymentModal.refund_reason && <p className="text-xs text-red-600 mt-1">{showPaymentModal.refund_reason}</p>}
                      <button
                        onClick={async () => {
                          try {
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pets/${showPaymentModal.id}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ refunded: false, refund_date: null, refund_amount: null, refund_reason: null })
                            });
                            if (response.ok) { await fetchPets(); showToast('Refund reversed'); setShowPaymentModal(null); }
                          } catch { showToast('Failed to reverse refund', 'error'); }
                        }}
                        className="mt-2 text-xs text-red-600 hover:underline"
                      >
                        Undo Refund
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <input
                        id="refund-amount-input"
                        type="number"
                        step="0.01"
                        defaultValue={showPaymentModal.total_cost || 0}
                        placeholder="Refund amount"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <input
                        id="refund-reason-input"
                        type="text"
                        placeholder="Reason (optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <button
                        onClick={async () => {
                          try {
                            const amount = parseFloat(document.getElementById('refund-amount-input').value) || 0;
                            const reason = document.getElementById('refund-reason-input').value || '';
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pets/${showPaymentModal.id}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ refunded: true, refund_date: new Date().toISOString(), refund_amount: amount, refund_reason: reason })
                            });
                            if (response.ok) { await fetchPets(); showToast(`Refund of $${amount} processed`); setShowPaymentModal(null); }
                          } catch { showToast('Failed to process refund', 'error'); }
                        }}
                        className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium text-sm"
                        data-testid="process-refund-btn"
                      >
                        Process Refund
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4 border-t">
                <button
                  onClick={() => {
                    setShowPaymentModal(null);
                    window.location.href = `/pets/${showPaymentModal.id}/edit`;
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg"
                >
                  Open Full Edit Form
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transportation Modal */}
      {showTransportationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Update Transportation</h3>
              <button
                onClick={() => setShowTransportationModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pet: {showTransportationModal.pet_name} ({showTransportationModal.client_name})
                </label>
              </div>

              {/* Quick Transportation Options */}
              <div className="space-y-3">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                    <span className="mr-2">🚐</span>Vet Collection
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setShowTransportationModal({ 
                          ...showTransportationModal, 
                          transportation_fees: ["Vet pick‑up (included) $0"] 
                        });
                      }}
                      className={`w-full text-left p-2 rounded border text-sm transition-colors ${
                        showTransportationModal.transportation_fees?.[0] === "Vet pick‑up (included) $0"
                          ? 'bg-blue-100 border-blue-400 text-blue-800 font-medium'
                          : 'bg-white border-gray-200 hover:bg-blue-50'
                      }`}
                    >
                      🚐 Vet pick-up (Included)
                    </button>
                    <button
                      onClick={() => {
                        setShowTransportationModal({ 
                          ...showTransportationModal, 
                          transportation_fees: ["Home pick‑up (business hours) $80"] 
                        });
                      }}
                      className={`w-full text-left p-2 rounded border text-sm transition-colors ${
                        showTransportationModal.transportation_fees?.[0] === "Home pick‑up (business hours) $80"
                          ? 'bg-blue-100 border-blue-400 text-blue-800 font-medium'
                          : 'bg-white border-gray-200 hover:bg-blue-50'
                      }`}
                    >
                      🏠 Home pick-up - $80
                    </button>
                  </div>
                </div>

                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <h4 className="text-sm font-semibold text-green-900 mb-2 flex items-center">
                    <span className="mr-2">🏠</span>Ashes Return
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setShowTransportationModal({ 
                          ...showTransportationModal, 
                          transportation_fees: ["Ashes deliver to home $40"] 
                        });
                      }}
                      className={`w-full text-left p-2 rounded border text-sm transition-colors ${
                        showTransportationModal.transportation_fees?.[0] === "Ashes deliver to home $40"
                          ? 'bg-green-100 border-green-400 text-green-800 font-medium'
                          : 'bg-white border-gray-200 hover:bg-green-50'
                      }`}
                    >
                      🏡 Ashes deliver to home - $40
                    </button>
                    <button
                      onClick={() => {
                        setShowTransportationModal({ 
                          ...showTransportationModal, 
                          transportation_fees: ["Ashes delivery to vet clinic $0"] 
                        });
                      }}
                      className={`w-full text-left p-2 rounded border text-sm transition-colors ${
                        showTransportationModal.transportation_fees?.[0] === "Ashes delivery to vet clinic $0"
                          ? 'bg-green-100 border-green-400 text-green-800 font-medium'
                          : 'bg-white border-gray-200 hover:bg-green-50'
                      }`}
                    >
                      🏥 Ashes delivery to vet clinic (Included)
                    </button>
                    <button
                      onClick={() => {
                        setShowTransportationModal({ 
                          ...showTransportationModal, 
                          transportation_fees: ["Customer collects from office $0"] 
                        });
                      }}
                      className={`w-full text-left p-2 rounded border text-sm transition-colors ${
                        showTransportationModal.transportation_fees?.[0] === "Customer collects from office $0"
                          ? 'bg-green-100 border-green-400 text-green-800 font-medium'
                          : 'bg-white border-gray-200 hover:bg-green-50'
                      }`}
                    >
                      🏢 Customer collects from office (Included)
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pets/${showTransportationModal.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ 
                          transportation_fees: showTransportationModal.transportation_fees 
                        })
                      });
                      if (response.ok) {
                        await fetchPets();
                        showToast('Transportation updated successfully 🚚');
                        setShowTransportationModal(null);
                      } else {
                        showToast('Failed to update transportation', 'error');
                      }
                    } catch (error) {
                      showToast('Failed to update transportation', 'error');
                    }
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setShowTransportationModal(null);
                    window.location.href = `/pets/${showTransportationModal.id}/edit`;
                  }}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg"
                >
                  Full Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Notes Modal */}
      {showQuickNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Quick Notes</h3>
              <button
                onClick={() => setShowQuickNotesModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pet: {showQuickNotesModal.pet_name} ({showQuickNotesModal.client_name})
                </label>
              </div>

              {/* Current Notes Display */}
              {showQuickNotesModal.jewelry_notes && showQuickNotesModal.jewelry_notes.trim() && (
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                  <p className="text-xs text-purple-600 font-medium mb-1">Current Notes:</p>
                  <div className="text-sm text-purple-900 whitespace-pre-line">
                    {formatNotesWithBullets(showQuickNotesModal.jewelry_notes)}
                  </div>
                </div>
              )}

              {/* Notes Textarea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  id="quick-notes-textarea"
                  defaultValue={showQuickNotesModal.jewelry_notes || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                  rows="6"
                  placeholder="Enter notes here... (Each line will display as a bullet point)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Each line will automatically display as a bullet point
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <button
                  onClick={async () => {
                    try {
                      const notesText = document.getElementById('quick-notes-textarea').value;
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pets/${showQuickNotesModal.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ 
                          jewelry_notes: notesText 
                        })
                      });
                      if (response.ok) {
                        await fetchPets();
                        showToast('Notes updated successfully 💬');
                        setShowQuickNotesModal(null);
                      } else {
                        showToast('Failed to update notes', 'error');
                      }
                    } catch (error) {
                      console.error('Error updating notes:', error);
                      showToast('Failed to update notes', 'error');
                    }
                  }}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  💾 Save Notes
                </button>
                <button
                  onClick={() => setShowQuickNotesModal(null)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <QuickLocationModal
          pet={showLocationModal}
          onClose={() => setShowLocationModal(null)}
          onSuccess={() => {
            setShowLocationModal(null);
            fetchPets();
            fetchStats();
            showToast('Location updated successfully! ✅');
          }}
        />
      )}

      {workflowPet && (
        <WorkflowModal
          key={workflowPet.id}
          pet={workflowPet}
          onClose={() => setWorkflowPet(null)}
          onSuccess={() => {
            console.log('🔄 WORKFLOW UPDATE SUCCESS - Should refresh pets now');
            setWorkflowPet(null);
            console.log('📞 CALLING fetchPets() after workflow update...');
            fetchPets();
            console.log('📞 CALLING fetchStats() after workflow update...');
            fetchStats();
            showToast('Workflow updated successfully! ✅');
          }}
          openSMSModal={openSMSModal}
          openEmailModal={openEmailModal}
          openCertificateModal={openCertificateModal}
        />
      )}

      {/* Order History Modal */}
      {showOnlineSales && (
        onlineSalesTab === 'new_order' ? (
          <OnlineSalesModal
            onClose={() => setShowOnlineSales(false)}
            onSwitchTab={(tab) => setOnlineSalesTab(tab)}
          />
        ) : (
          <OrderHistoryModal
            onClose={() => setShowOnlineSales(false)}
            showToast={showToast}
            onSwitchTab={(tab) => setOnlineSalesTab(tab)}
          />
        )
      )}
      
      {/* Calendar Modal */}
      {showCalendar && (
        <CalendarView
          onClose={() => {
            setShowCalendar(false);
            setCalendarInitialView('grid');
          }}
          onAppointmentUpdate={() => {
            // Refresh pets and stats when appointment is created/updated
            fetchPets();
            fetchStats();
          }}
          initialView={calendarInitialView}
        />
      )}

      {/* Walk-in Form Modal */}
      {showWalkInForm && (
        <WalkInFormModal
          onClose={() => setShowWalkInForm(false)}
          onSuccess={() => {
            setShowWalkInForm(false);
            setShowThankYou(true);
            // Refresh data to show new pet
            setTimeout(() => {
              fetchPets();
              fetchStats();
            }, 300);
          }}
        />
      )}

      {/* Thank You Modal */}
      {showThankYou && (
        <ThankYouModal
          onClose={() => setShowThankYou(false)}
        />
      )}

      {/* SMS Preview Modal */}
      <SMSPreviewModal
        isOpen={showSMSModal}
        onClose={() => {
          setShowSMSModal(false);
          setSMSModalData({
            petId: null,
            stepKey: null,
            templateKey: null,
            primaryTemplate: null,
            alternativeTemplates: [],
            renderedBody: '',
            clientName: '',
            petName: '',
            toPhone: '',
            loading: false,
            sending: false,
            error: null,
            success: false,
            isCertificateEmail: false
          });
        }}
        modalData={smsModalData}
        onTemplateChange={changeTemplate}
        onMessageEdit={(newBody) => setSMSModalData(prev => ({ ...prev, renderedBody: newBody }))}
        onSend={sendSMS}
      />


      {/* Email Preview Modal */}
      <EmailPreviewModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setEmailModalData({
            petId: null,
            stepKey: null,
            templateKey: null,
            subject: '',
            renderedBody: '',
            clientName: '',
            petName: '',
            toEmail: '',
            loading: false,
            sending: false,
            error: null,
            success: false,
            isCertificateEmail: false,
            availableTemplates: [],
            previousPetInfo: null
          });
        }}
        modalData={emailModalData}
        onSignatureUpdate={setEmailModalData}
        onSubjectEdit={(newSubject) => setEmailModalData(prev => ({ ...prev, subject: newSubject }))}
        onMessageEdit={(newBody) => setEmailModalData(prev => ({ ...prev, renderedBody: typeof newBody === 'function' ? newBody(prev.renderedBody) : newBody }))}
        onSend={emailModalData.isCertificateEmail ? sendCertificates : sendEmail}
        onTemplateChange={changeEmailTemplate}
        onAfterpayLinkChange={link => setEmailModalData(prev => updateAfterpayDraft(prev, link))}
      />

      {/* AI Chat Assistant — floating panel */}
      <AIChatPanel contextType="dashboard" onRefresh={fetchPets} />

      {/* Cremators Operations */}
      {showPet400 && <Pet400View onClose={() => setShowPet400(false)} />}

      {/* Inventory Management */}
      <InventoryModal isOpen={showInventory} onClose={() => setShowInventory(false)} />
      <PricingAdminModal isOpen={showPricing} onClose={() => setShowPricing(false)} />
      <NotificationSettingsModal isOpen={showNotificationSettings} onClose={() => setShowNotificationSettings(false)} userEmail={user?.email} />

    </div>
  );
};


export default Dashboard;
